/**
 * Fonte do dicionário da UI do painel administrativo (chaves `admin.*`).
 *
 * Fonte ÚNICA: este arquivo alimenta (a) o fallback local do provider — o
 * painel nunca exibe chave crua, nem logado sem rede, nem na página de login
 * antes da autenticação — e (b) os seeds da migração SQL (packages/api/
 * drizzle/migrations/0028), gerada a partir dele. Depois de semeadas, as
 * traduções vivem no banco (ui_texts): edição manual vence, e o botão
 * "Atualizar" da plataforma completa os outros idiomas via LLM.
 *
 * PT = fonte; EN = rede de segurança embutida (as demais bases ficam a
 * cargo do pipeline LLM e chegam pelo endpoint /admin/i18n/dictionary).
 */

export const ADMIN_PT: Record<string, string> = {
  // ── navegação / shell ──
  'admin.nav.dashboard': 'Início',
  'admin.nav.kitchen': 'Cozinha',
  'admin.nav.waiter': 'Garçom',
  'admin.nav.branding': 'Branding',
  'admin.nav.menu': 'Menu',
  'admin.nav.translations': 'Traduções',
  'admin.nav.combos': 'Combos',
  'admin.nav.taxes': 'Impostos',
  'admin.nav.tables': 'Mesas',
  'admin.nav.users': 'Usuários',
  'admin.nav.platform': 'Plataforma',
  'admin.nav.platform_users': 'Usuários da plataforma',
  'admin.nav.platform_tenants': 'Restaurantes',
  'admin.nav.platform_translations': 'Traduções da plataforma',
  'admin.nav.logout': 'Sair',
  'admin.nav.section.operations': 'Operação',
  'admin.nav.section.management': 'Gestão',
  'admin.nav.section.platform': 'Plataforma',
  'admin.nav.language': 'Idioma',
  'admin.nav.collapse': 'Recolher barra lateral',
  'admin.nav.expand': 'Mostrar barra lateral',
  'admin.country.pt': 'Portugal',
  'admin.country.br': 'Brasil',
  'admin.country.us': 'Estados Unidos',
  'admin.country.es': 'Espanha',

  // ── comuns ──
  'admin.common.save': 'Guardar',
  'admin.common.create': 'Criar',
  'admin.common.saving': 'Guardando…',
  'admin.common.cancel': 'Cancelar',
  'admin.common.close': 'Fechar',
  'admin.common.delete': 'Excluir',
  'admin.common.edit': 'Editar',
  'admin.common.new': 'Novo',
  'admin.common.confirm': 'Confirmar',
  'admin.common.search': 'Buscar',
  'admin.common.loading': 'Carregando…',
  'admin.common.empty': 'Nada por aqui ainda',
  'admin.common.error': 'Erro ao carregar',
  'admin.common.back': 'Voltar',
  'admin.common.active': 'Ativo',
  'admin.common.inactive': 'Inativo',
  'admin.common.yes': 'Sim',
  'admin.common.no': 'Não',
  'admin.common.minutes': 'min',

  // ── status de pedido (garçom + cozinha) ──
  'admin.status.pending': 'Novo',
  'admin.status.confirmed': 'Confirmado',
  'admin.status.preparing': 'Em preparo',
  'admin.status.ready': 'Pronto',
  'admin.status.delivered': 'Entregue',
  'admin.status.cancelled': 'Cancelado',

  // ── canais ──
  'admin.channel.takeaway': '🛍 Take-away',
  'admin.channel.delivery': '🚚 Delivery',

  // ── pedidos (comum garçom/cozinha) ──
  'admin.orders.table': 'Mesa',
  'admin.orders.customer': 'Cliente',
  'admin.orders.done': 'feito',
  'admin.orders.notes': 'obs',
  'admin.orders.without': 'sem',
  'admin.orders.entered_at': 'Entrada',
  'admin.orders.confirm': 'Confirmar',
  'admin.orders.deliver': 'Entregue',
  'admin.orders.cancel': 'Cancelar',
  'admin.orders.mark_paid': 'Conta paga',
  'admin.orders.transfer': 'Transferir mesa',
  'admin.orders.edit': 'Editar',
  'admin.orders.invalid_move': 'Movimento inválido: {from} → {to}.',
  'admin.orders.kitchen_only': 'Esta transição é da cozinha — use o quadro de preparo.',
  'admin.orders.ready_toast': 'Pedido pronto para servir',
  'admin.orders.moved_toast': 'Pedido movido para a mesa {table}',
  'admin.orders.paid_toast': 'Conta marcada como paga',
  'admin.orders.paid_err': 'Erro ao marcar pago',
  'admin.orders.reorder_err': 'Erro ao reordenar',

  // ── página do garçom ──
  'admin.waiter.hint': 'Arraste entre colunas para avançar · dentro da coluna para priorizar',
  'admin.waiter.rt_on': 'tempo real ativo',
  'admin.waiter.rt_off': 'reconectando…',
  'admin.waiter.open': 'restaurante aberto',
  'admin.waiter.closed': 'restaurante fechado',
  // Web Push (#124 PR-3): controlo do dispositivo (estados do pushSupport) e
  // conteúdo das notificações montado na API no idioma do destinatário.
  // Textos do controlo: orientação de UX da #124, aceites pelo PO.
  'admin.push.enable': 'Ativar notificações',
  'admin.push.scope_note':
    'Avisa quando um prato fica pronto ou uma mesa chama, mesmo com a app fechada. Toda a equipe do Salão com notificações ativas recebe os mesmos avisos.',
  'admin.push.enabled': 'Notificações ativas',
  'admin.push.blocked':
    'Notificações bloqueadas no browser. Permita as notificações da Pratzu nas opções do browser.',
  'admin.push.unsupported_install': 'Instale a app para receber notificações',
  'admin.push.install_ios_steps':
    'No iPhone ou iPad, abra a Pratzu no Safari, toque em Partilhar e escolha adicionar à página de início. Depois, abra a Pratzu pelo ícone novo e ative as notificações.',
  'admin.push.unsupported':
    'Este browser não recebe notificações. Os avisos aparecem só com a Pratzu aberta.',
  'admin.push.error': 'Não foi possível ativar as notificações. Tente novamente.',
  // Conteúdo das notificações (API, features/push/texts.ts): só mesa/estado,
  // NUNCA dados do cliente (critério 6 da #124). {n} = número da mesa.
  'admin.push.order_new_title': 'Novo pedido',
  'admin.push.order_new_body': 'Pedido no salão',
  'admin.push.kitchen_new_title': 'Novo pedido',
  'admin.push.kitchen_new_body': 'Pedido para preparar',
  'admin.push.order_ready_title': 'Pedido pronto',
  'admin.push.order_ready_body': 'Pedido pronto para servir',
  'admin.push.call_waiter_title': 'Mesa chamando',
  'admin.push.call_waiter_body': 'Chamada no salão',
  'admin.push.table_number': 'Mesa {n}',
  'admin.push.channel_takeaway': 'Retirada',
  'admin.push.channel_delivery': 'Entrega',
  // modo garçom (0085): visão por mesa — pedidos enviados pelo cliente
  'admin.waiter.flow_title': 'Pedidos por mesa',
  'admin.waiter.flow_empty': 'Nenhum pedido enviado',
  'admin.waiter.flow_sent': 'Enviado',
  'admin.waiter.flow_ack': 'Recebido',
  'admin.waiter.flow_relayed': 'Lançado no local',
  'admin.waiter.flow_ack_action': 'Confirmar recebimento',
  'admin.waiter.flow_relay_action': 'Lançar no sistema local',
  'admin.waiter.bills_title': 'Contas a receber',
  'admin.waiter.bills_empty': 'Nenhuma conta aguardando pagamento.',
  'admin.waiter.bill_confirm': 'Confirmar recebimento',
  'admin.waiter.bill_cancel': 'Cancelar conta',
  'admin.waiter.bill_cancel_toast': 'Conta cancelada.',
  'admin.waiter.bill_paid_toast': 'Conta confirmada como paga.',
  'admin.waiter.bill_mode_single': 'Uma pessoa paga tudo',
  'admin.waiter.bill_mode_by_people': 'Dividida por pessoas',
  'admin.waiter.bill_mode_flexible': 'Divisão flexível',
  'admin.waiter.bill_tip': 'Gorjeta',
  'admin.waiter.pay_online': 'Pago online',
  'admin.waiter.paid_online_badge': 'pago online',
  'admin.waiter.online_paid': 'Já pago online',
  'admin.waiter.online_processing': 'Processando',
  'admin.waiter.online_due': 'A receber',
  'admin.waiter.payment_pending': 'pendente',
  'admin.waiter.payment_processing': 'processando',
  'admin.waiter.payment_succeeded': 'pago',
  'admin.waiter.payment_failed': 'falhou',
  'admin.waiter.payment_canceled': 'cancelado',
  'admin.waiter.payment_refunded': 'reembolsado {amount}',
  'admin.waiter.bill_refund': 'Reembolsar',
  'admin.waiter.bill_refund_confirm': 'Devolver {amount} ao cliente',
  'admin.waiter.bill_refund_done': 'Reembolso efetuado ({id})',
  'admin.waiter.bill_refund_toast': 'Pagamento reembolsado.',
  'admin.waiter.bill_part_paid_toast': 'Uma parte da conta foi paga online.',
  'admin.payments.title': 'Pagamento online',
  'admin.payments.subtitle':
    'Receba a conta pelo telefone: o cliente paga a parte dele e o valor cai na conta do restaurante via Stripe.',
  'admin.payments.state_ready': 'Pronto para receber',
  'admin.payments.state_pending': 'Onboarding em andamento',
  'admin.payments.state_off': 'Não configurado',
  'admin.payments.charges': 'Cobranças habilitadas',
  'admin.payments.payouts': 'Repasses habilitados',
  'admin.payments.details': 'Dados do restaurante completos',
  'admin.payments.start': 'Ativar pagamento online',
  'admin.payments.resume': 'Continuar configuração',
  'admin.payments.hint': 'A ativação leva ~5 min no Stripe. O estado atualiza sozinho ao terminar.',
  'admin.nav.payments': 'Pagamentos',
  'admin.location.title': 'Localização',
  'admin.location.subtitle':
    'Endereço do restaurante no mapa — usado pelos clientes para achá-lo e pelo link "Abrir no Google Maps".',
  'admin.location.current': 'Localização atual',
  'admin.location.none': 'Nenhum endereço confirmado ainda.',
  'admin.location.state_confirmed': 'Confirmada',
  'admin.location.state_pending': 'Pendente de confirmação',
  'admin.location.confirmed_at': 'Confirmada em {date}.',
  'admin.location.suggestion_title': 'Sugestão encontrada para o seu restaurante',
  'admin.location.suggestion_confidence': 'Confiança da busca: {pct}%',
  'admin.location.confirm': 'Confirmar localização',
  'admin.location.confirmed': 'Localização confirmada.',
  'admin.location.change': 'Alterar endereço',
  'admin.location.open_maps': 'Abrir no Google Maps',
  'admin.location.search_label': 'Buscar endereço',
  'admin.location.search_placeholder': 'Nome da rua e número',
  'admin.location.search_unavailable':
    'Busca indisponível agora. Tente de novo ou digite manualmente.',
  'admin.location.manual_toggle': 'Digitar manualmente',
  'admin.location.search_toggle': 'Buscar no mapa',
  'admin.location.street': 'Rua e número',
  'admin.location.postal': 'Código postal',
  'admin.location.city': 'Cidade',
  'admin.location.country': 'País',
  'admin.location.pin_lat': 'Latitude (pino)',
  'admin.location.pin_lng': 'Longitude (pino)',
  'admin.location.pin_hint':
    'Ajuste as coordenadas se a entrada fica em outro ponto da quadra (dark kitchen, fundo do prédio…).',
  'admin.location.manual_hint':
    'Sem lugar no mapa: clientes não veem rota até você. Vale para dark kitchens e endereços não públicos.',
  'admin.location.manual_invalid': 'Preencha rua e cidade.',
  'admin.location.confirm_check': 'Confirmo que esta é a localização correta do restaurante.',
  'admin.location.save': 'Guardar localização',
  'admin.location.saved': 'Localização atualizada.',
  'admin.location.banner_title': 'Confirme a localização do seu restaurante.',
  'admin.location.banner_body_suggestion': 'Encontramos um endereço provável — confira e confirme.',
  'admin.location.banner_body_missing': 'Seus clientes não conseguem te encontrar no mapa ainda.',
  'admin.location.banner_cta': 'Confirmar agora',
  'admin.location.banner_link': 'Ver localização',
  'admin.nav.location': 'Localização',

  // ── país e moeda do restaurante (#486 item 1) ──
  'admin.nav.country_currency': 'País e moeda',
  'admin.country_currency.title': 'País e moeda',
  'admin.country_currency.subtitle':
    'O país e a moeda que o restaurante usa nos preços do menu. Todos os países do mundo estão na lista.',
  'admin.country_currency.country': 'País do restaurante',
  'admin.country_currency.currency': 'Moeda',
  'admin.country_currency.currency_hint':
    'A moeda acompanha o país escolhido. Escolha outra se o restaurante cobrar numa moeda diferente.',
  'admin.country_currency.no_conversion':
    'Mudar o país não converte preços existentes. Os novos itens nascem na moeda nova.',
  'admin.country_currency.confirm_title': 'Confirmar mudança',
  'admin.country_currency.confirm_country': 'País: {from} → {to}',
  'admin.country_currency.confirm_currency': 'Moeda: {from} → {to}',
  'admin.country_currency.confirm_no_conversion':
    'Os preços guardados não mudam. Os novos itens nascem na moeda nova.',
  'admin.country_currency.saved': 'País e moeda guardados.',
  'admin.waiter.bill_people': 'pessoas',
  'admin.waiter.bill_total': 'Total da conta',
  'admin.waiter.to_pay': '{count} conta(s) a receber',
  'admin.waiter.calling': '{count} mesa(s) chamando',
  'admin.waiter.new_order': '+ Novo pedido',
  'admin.waiter.empty': 'Nenhum pedido em andamento',
  'admin.waiter.empty_hint': 'Os pedidos aparecem aqui sozinhos.',
  'admin.waiter.col.pending': 'Novos',
  'admin.waiter.col.confirmed': 'Confirmados',
  'admin.waiter.col.preparing': 'Em preparo',
  'admin.waiter.col.ready': 'Prontos',
  'admin.waiter.col.to_pay': 'A pagar',

  // ── chamadas de garçom ──
  'admin.calls.calling': 'Mesa {table} está chamando',
  'admin.calls.waiting': 'chamando há {time}',
  'admin.calls.attend': 'Atendido',
  'admin.calls.attend_err': 'Erro ao atender',

  // ── página da cozinha ──
  'admin.kitchen.hint': 'Arraste entre colunas para avançar · dentro da coluna para priorizar',
  'admin.kitchen.new_order_toast': 'Novo pedido recebido',
  'admin.kitchen.status_err': 'Erro ao atualizar status',
  'admin.kitchen.item_err': 'Erro ao marcar item',
  'admin.kitchen.all_done_toast': 'Todos os itens concluídos — garçons notificados',
  'admin.kitchen.filter_active': 'Ativos',
  'admin.kitchen.filter_all': 'Todos',
  'admin.kitchen.empty': 'Sem pedidos em fila',
  'admin.kitchen.empty_hint': 'Novos pedidos aparecem aqui sozinhos.',
  'admin.kitchen.col.pending': 'Novos',
  'admin.kitchen.col.confirmed': 'Confirmados',
  'admin.kitchen.col.preparing': 'Em preparo',
  'admin.kitchen.col.ready': 'Prontos',
  'admin.kitchen.col.delivered': 'Entregues',
  'admin.kitchen.col.cancelled': 'Cancelados',
  'admin.kitchen.waiter_only': 'Cozinha não executa esta transição — use o painel do garçom.',
  'admin.kitchen.finish_item': 'Concluir',
  'admin.kitchen.start_preparing': 'Iniciar preparo',
  'admin.kitchen.mark_ready': 'Marcar pronto',
  'admin.kitchen.ready_waiting': 'Pronto — aguardando garçom entregar',

  // ── login ──
  'admin.login.subtitle': 'Painel do Restaurante',
  'admin.login.title': 'Entrar',
  'admin.login.email_hint': 'Informe seu e-mail para receber o código de acesso.',
  'admin.login.google_btn': 'Continuar com Google',
  'admin.login.or': 'ou',
  'admin.login.email': 'E-mail',
  'admin.login.email_ph': 'seuemail@restaurante.com',
  'admin.login.send_code': 'Enviar código',
  'admin.login.verify_title': 'Verificar código',
  'admin.login.code_sent_prefix': 'Código enviado para',
  'admin.login.code_sent_to': 'Código enviado para {email}',
  'admin.login.code_label': 'Código de 6 dígitos',
  'admin.login.use_another_email': 'Usar outro e-mail',
  'admin.login.choose_title': 'Escolher acesso',
  'admin.login.choose_hint': 'Onde quer entrar,',
  'admin.login.enter_as': 'Entrar como {role}',
  'admin.login.role_owner': 'Dono',
  'admin.login.role_platform_owner': 'Platform Owner',
  'admin.login.google_err':
    'Login Google não concluído — tente novamente ou use o código por e-mail.',
  'admin.login.google_failed': 'Login Google falhou',
  'admin.login.no_access':
    'Nenhum acesso encontrado para este e-mail. Peça ao dono do restaurante para convidá-lo em Usuários (Cozinha, Garçom ou Dono).',
  'admin.login.send_code_err': 'Erro ao enviar código',
  'admin.login.invalid_code': 'Código inválido ou expirado',
  'admin.login.signin_err': 'Erro ao entrar',

  // ── dashboard / home ──
  'admin.dashboard.welcome_title': 'Bem-vindo à plataforma',
  'admin.dashboard.welcome_hint': 'Selecione um restaurante no menu lateral para começar.',
  'admin.dashboard.default_name': 'Meu Restaurante',
  'admin.dashboard.subtitle': 'Painel do Restaurant Owner',
  'admin.dashboard.open': '● Aberto',
  'admin.dashboard.closed': '● Fechado',
  'admin.dashboard.stats_categories': 'Categorias ativas',
  'admin.dashboard.stats_products': 'Produtos ativos',
  'admin.dashboard.stats_tables': 'Mesas ativas',
  'admin.dashboard.quick_actions': 'Ações rápidas',
  'admin.dashboard.link_branding': 'Configurar branding',
  'admin.dashboard.link_branding_desc': 'Cores, logo e horários',
  'admin.dashboard.link_menu': 'Gerenciar menu',
  'admin.dashboard.link_menu_desc': 'Categorias e produtos',
  'admin.dashboard.link_combos': 'Combos e promoções',
  'admin.dashboard.link_combos_desc': 'Descontos agrupados',
  'admin.dashboard.link_tables': 'Gerar mesas e QR codes',
  'admin.dashboard.link_tables_desc': 'Mesas com QR imprimível',
  'admin.dashboard.view_menu': 'Visualizar menu',
  'admin.dashboard.takeaway_delivery': 'Take-away / Delivery',
  'admin.dashboard.takeaway_delivery_desc': 'Abre o menu público de encomendas',
  'admin.dashboard.table_menu': 'Menu de mesa',
  'admin.dashboard.table_menu_desc': 'Escolha uma mesa e abra o menu',
  'admin.dashboard.status': 'Status',
  'admin.dashboard.primary_locale': 'Idioma principal',
  'admin.dashboard.takeaway': 'Take-away',
  'admin.dashboard.delivery': 'Delivery',
  'admin.dashboard.tip': 'Gorjeta',
  'admin.dashboard.tables_total': 'Mesas totais',
  'admin.dashboard.choose_table': 'Escolher mesa',
  'admin.dashboard.no_active_tables': 'Nenhuma mesa ativa.',
  'admin.dashboard.create_tables': 'Criar mesas →',
  'admin.dashboard.table_number': 'Mesa {number}',

  // ── mesas & QR codes ──
  'admin.nav.kitchens': 'Cozinhas',
  'admin.kitchens.title': 'Cozinhas (estações)',
  'admin.kitchens.subtitle':
    'Divida o preparo em estações — cada cozinha vê só os seus itens no monitor',
  'admin.kitchens.main': 'Cozinha principal',
  'admin.kitchens.main_hint': 'Produtos sem direcionamento preparam aqui (padrão)',
  'admin.kitchens.products': '{n} produtos',
  'admin.kitchens.name_placeholder': 'Ex.: Bar, Carnes, Sobremesas',
  'admin.kitchens.create': '+ Criar cozinha',
  'admin.kitchens.created': 'Cozinha criada',
  'admin.kitchens.deleted': 'Cozinha excluída — produtos voltaram para a principal',
  'admin.kitchens.confirm_delete': 'Excluir "{name}"? Os produtos voltam para a cozinha principal.',
  'admin.kitchens.empty': 'Só a cozinha principal',
  'admin.kitchens.empty_hint': 'Crie estações (ex.: bar, carnes) para direcionar os produtos',
  'admin.kitchens.active': 'ativa',
  'admin.kitchens.inactive': 'inativa',
  'admin.kitchens.delete': 'excluir',
  'admin.kitchens.assign_title': 'Direcionar categoria',
  'admin.kitchens.assign_hint': 'Move todos os produtos da categoria para a cozinha escolhida',
  'admin.kitchens.assign_apply': 'Aplicar',
  'admin.kitchens.assigned': '{n} produtos direcionados',
  'admin.kitchens.no_categories': 'Crie uma categoria primeiro',
  'admin.kitchen.all_stations': 'Todas',
  'admin.kitchen.waiting_stations': 'Aguardando outras cozinhas',
  'admin.kitchen.ready_blocked':
    'O pedido só fica pronto quando todas as cozinhas concluírem seus itens',
  'admin.menu.kitchen': 'Cozinha',
  'admin.menu.kitchen_hint': 'Onde este produto é preparado',
  'admin.tables.title': 'Mesas & QR Codes',
  'admin.tables.subtitle': 'Gerencie mesas e imprima QR codes',
  'admin.tables.print_all': '🖨 Imprimir todos',
  'admin.tables.generate_cta': '+ Gerar mesas',
  'admin.tables.generate_tables': 'Gerar mesas',
  'admin.tables.generate': 'Gerar',
  'admin.tables.empty': 'Nenhuma mesa cadastrada',
  'admin.tables.empty_hint': 'Gere mesas para criar os QR codes',
  'admin.tables.stat_active': 'Ativas',
  'admin.tables.stat_inactive': 'Inativas',
  'admin.tables.activate': 'Ativar',
  'admin.tables.deactivate': 'Desativar',
  'admin.tables.mesa': 'Mesa {n}',
  'admin.tables.qr_alt': 'QR Mesa {n}',
  'admin.tables.scan_to_order': 'Escaneie para pedir',
  'admin.tables.scan_hint': 'Aponte a câmera do celular para o QR code',
  'admin.tables.created_toast': '{count} mesas criadas!',
  'admin.tables.qty': 'Quantidade de mesas',
  'admin.tables.qty_hint': 'Máximo 200 por vez',
  'admin.tables.start_from': 'Numerar a partir de',
  'admin.tables.start_from_hint': 'Números já existentes são pulados',
  'admin.tables.preview':
    'Serão criadas {count} mesas numeradas a partir de {start}. Cada mesa recebe um QR code único.',
  'admin.tables.qr_print_err': 'Erro ao gerar QRs',
  'admin.tables.regen_ok': 'QR regenerado!',
  'admin.tables.qr_title': 'QR Code — Mesa {n}',
  'admin.tables.qr_url': 'URL do QR',
  'admin.tables.regen': 'Regenerar QR',
  'admin.tables.download_png': 'Baixar PNG',
  'admin.tables.regen_hint':
    'Regenerar invalida o QR code anterior. Use apenas se o atual foi comprometido.',

  // ── transferência de mesa ──
  'admin.transfer.moved_toast': 'Pedido movido para a {table}',
  'admin.transfer.err': 'Erro ao transferir',
  'admin.transfer.transferring': 'Transferindo…',
  'admin.transfer.submit': 'Transferir',
  'admin.transfer.hint_start': 'Pedido de',
  'admin.transfer.hint_end':
    'será movido para a mesa escolhida. As páginas da cozinha e do salão atualizam sozinhas.',
  'admin.transfer.no_options': 'Não há outras mesas ativas para transferir.',
  'admin.transfer.target': 'Mesa de destino',

  // ── kanban ──
  'admin.kanban.empty': 'Sem pedidos',
  'admin.kanban.order_count': '{count} pedido(s)',

  // ── branding ──
  'admin.branding.subtitle': 'Identidade visual e configurações do restaurante',
  'admin.branding.menu_template': 'Template do menu',
  'admin.branding.menu_template_desc': 'Como o menu aparece para o cliente.',
  'admin.branding.menu_template_list': 'Lista (clássico)',
  'admin.branding.menu_template_list_desc': 'Lista compacta com foto pequena à direita.',
  'admin.branding.menu_template_grid': 'Grade com fotos grandes',
  'admin.branding.menu_template_grid_desc':
    'Duas fotos por linha, carrossel no produto e filtros de alergias.',
  'admin.branding.menu_template_feed1': 'Feed (1 foto por linha)',
  'admin.branding.menu_template_feed1_desc':
    'Cada prato vira um post com foto grande em página cheia e carrossel.',
  'admin.branding.saved_toast': 'Guardado!',
  'admin.branding.general_title': 'Informações gerais',
  'admin.branding.about': 'Sobre o restaurante',
  'admin.branding.about_ph': 'Breve descrição exibida no menu do cliente…',
  'admin.branding.logo': 'Logo',
  'admin.branding.logo_updated_toast': 'Logo atualizado!',
  'admin.branding.logo_change': 'Trocar logo',
  'admin.branding.logo_upload': 'Enviar logo',
  'admin.branding.logo_hint': 'JPEG, PNG ou WebP · máx 5MB',
  'admin.branding.locale_updated_toast': 'Idioma atualizado!',
  'admin.branding.locale_title': 'Idioma e moeda',
  'admin.branding.locale_desc': 'Define o idioma do menu e a moeda exibida nos preços',
  'admin.branding.primary_locale': 'Idioma principal',
  'admin.branding.save_locale': 'Guardar idioma',
  'admin.branding.locales_saved_toast': 'Idiomas guardados — gerando as traduções faltantes',
  'admin.branding.retranslation_toast':
    'Re-tradução iniciada — {n} rascunho(s) regenerados do zero',
  'admin.branding.target_locales_title': 'Idiomas de tradução',
  'admin.branding.target_locales_desc':
    'Idiomas em que o menu é traduzido (produtos, ingredientes, variações e adicionais)',
  'admin.branding.target_locales_hint':
    'O idioma principal ({primary}) é a base — não é traduzido. Guardar gera as traduções faltantes (pode levar alguns minutos para menus grandes ou muitos idiomas); itens novos são traduzidos ao serem criados ou editados.',
  'admin.branding.save_target_locales': 'Guardar idiomas de tradução',
  'admin.branding.refresh_translations': 'Atualizar traduções',
  'admin.branding.translations_status': 'Status das traduções',
  'admin.branding.colors_published_toast': 'Cores publicadas no menu!',
  'admin.branding.color_background': 'Fundo da loja',
  'admin.branding.color_background_help':
    'Cor de fundo usada em todo o menu do cliente — lista de produtos, detalhe do produto e carrinho.',
  'admin.branding.color_header': 'Cabeçalho',
  'admin.branding.color_header_help':
    'Fundo da faixa superior com o logo e o nome do restaurante, na página inicial do menu.',
  'admin.branding.color_button': 'Botões',
  'admin.branding.color_button_help':
    'Cor dos botões principais do menu, como "Adicionar ao pedido" e as categorias em destaque.',
  'admin.branding.color_button_active': 'Botão pressionado',
  'admin.branding.color_button_active_help':
    'Cor exibida por um instante quando o cliente toca em um botão — feedback visual de toque.',
  'admin.branding.color_selected': 'Seleção',
  'admin.branding.color_selected_help':
    'Cor usada para indicar categorias, variações ou opções que estão selecionadas no momento.',
  'admin.branding.color_logo_bg': 'Fundo do destaque da logo',
  'admin.branding.color_logo_bg_help':
    'Fundo da área de destaque no topo do menu. Preenchido ao enviar a logo — vazio = detectar da imagem.',
  'admin.branding.colors_title': 'Paleta de cores',
  'admin.branding.colors_desc':
    'Veja o preview antes de publicar — as cores só valem para o menu do cliente depois de clicar em Guardar',
  'admin.branding.color_auto_ph': 'auto (da logo)',
  'admin.branding.color_use_auto': 'usar auto',
  'admin.branding.colors_publish': 'Guardar e publicar no menu',
  'admin.branding.colors_reset': 'Restaurar padrão',
  'admin.branding.colors_preview_hint':
    'Pré-visualização — toque e segure o botão para ver a cor de "pressionado"',
  'admin.branding.ops_open': 'Restaurante aberto',
  'admin.branding.ops_open_desc': 'Controle manual independente de horário',
  // Modo de abertura (#332, 0080): substitui o toggle por 3 opções
  'admin.branding.ops_open_mode': 'Modo de abertura',
  'admin.branding.ops_open_mode_auto': 'Pelo horário',
  'admin.branding.ops_open_mode_open': 'Aberto agora',
  'admin.branding.ops_open_mode_closed': 'Fechado',
  'admin.branding.ops_open_mode_auto_desc': 'Abre e fecha conforme o horário de funcionamento.',
  'admin.branding.ops_open_mode_open_desc': 'Aceita pedidos agora, mesmo fora do horário.',
  'admin.branding.ops_open_mode_closed_desc': 'Não aceita pedidos, mesmo dentro do horário.',
  'admin.branding.ops_takeaway': 'Take-away',
  'admin.branding.ops_takeaway_desc': 'Aceitar pedidos para retirada',
  'admin.branding.ops_delivery': 'Delivery',
  'admin.branding.ops_delivery_desc': 'Aceitar pedidos para entrega',
  // fluxo dos pedidos da mesa (0085): completo × via garçom (POS local)
  'admin.branding.ops_order_flow': 'Fluxo dos pedidos da mesa',
  'admin.branding.ops_order_flow_full': 'Completo (cozinha e salão)',
  'admin.branding.ops_order_flow_full_desc': 'O pedido entra na cozinha e no salão, como hoje.',
  'admin.branding.ops_order_flow_waiter': 'Via garçom (sistema local)',
  'admin.branding.ops_order_flow_waiter_desc':
    'O cliente envia o pedido ao garçom, que lança no sistema local do restaurante.',
  'admin.branding.ops_tip_mode': 'Modo de gorjeta',
  'admin.branding.ops_tip_none': 'Sem gorjeta',
  'admin.branding.ops_tip_optional': 'Opcional',
  'admin.branding.ops_tip_suggested': 'Sugerida',
  'admin.branding.ops_fiscal_mode': 'Identificação fiscal no checkout',
  'admin.branding.ops_fiscal_mode_desc':
    'Pedir NIF/CPF do cliente no fechamento do pedido — sai no recibo fiscal.',
  'admin.branding.ops_fiscal_off': 'Não pedir',
  'admin.branding.ops_fiscal_optional': 'Opcional (campo livre)',
  'admin.branding.ops_fiscal_required': 'Obrigatório para o cliente',
  'admin.branding.ops_sla': 'SLA de espera da cozinha (min)',
  'admin.branding.ops_sla_desc':
    'Pedidos esperando mais que isso ganham destaque vermelho no monitor da cozinha.',
  'admin.branding.ops_sla_invalid': 'Informe o SLA em minutos (1 a 240).',
  'admin.branding.ops_save': 'Guardar operação',
  // #132: garçom avançado
  'admin.waiter.my_tables': 'Minhas mesas',
  'admin.waiter.my_tables_hint': 'Toque nas mesas que você atende para filtrar o salão.',
  'admin.waiter.my_tables_none': 'Toque numa mesa para assumir o atendimento',
  'admin.waiter.my_tables_all': 'Ver todas as mesas',
  'admin.waiter.my_tables_mine': 'Só as minhas',
  'admin.tables.merge': 'Unir com…',
  'admin.tables.merge_title': 'Unir mesa {from} na mesa {to}',
  'admin.tables.merge_hint':
    'Os pedidos em andamento e a conta ativa da mesa {from} passam para a mesa {to}. A mesa {from} fica inativa. Ação auditada.',
  'admin.tables.merge_done': 'Mesas unidas: {from} → {to}',
  'admin.tables.merge_pick': 'Mesa de destino',
  'admin.tables.merge_no_target': 'Não há outra mesa ativa para receber esta.',
  'admin.import.review_item_ok': 'Correto',
  'admin.import.review_item_fix': 'Precisa de ajuste',
  'admin.import.review_item_removed': 'Removido',
  'admin.import.review_pending': 'Pendente',
  'admin.import.review_comment_ph': 'O que precisa remover, adicionar ou alterar neste item?',
  'admin.import.review_group_ph': 'Comentário para o grupo (aplica a todos os itens)',
  'admin.import.review_mark_all': 'Marcar todos como corretos',
  'admin.import.review_progress': 'Revisados {n} de {total}',
  'admin.import.review_submit': 'Enviar revisão',
  'admin.import.review_submit_hint':
    'Confirme cada item com ✓, comente o que precisa ajustar ou remova — depois envie tudo de uma vez.',
  'admin.import.review_fixed': 'ajuste aplicado',
  'admin.import.review_undo_remove': 'Desfazer remoção',
  'admin.import.reviewing_title': 'Aplicando seus comentários… {done} de {total}',
  'admin.import.reviewing_hint':
    'Quando terminar, a nova versão aparece aqui para você conferir antes de importar.',
  'admin.import.review_group_fix': 'Marcar todos como precisando de ajuste',
  'admin.import.review_group_hint':
    'O comentário do grupo vale para todos os itens dele — itens com comentário próprio têm prioridade.',
  'admin.import.translation_found': 'Tradução {locale} impressa no menu',
  'admin.import.kcal_label': 'kcal',
  'admin.import.service_charge_found':
    'Taxa de serviço lida no menu: {charge} — sugerida como informação, nunca cobrada sem confirmação.',
  'admin.import.review_modal_title': 'Itens sem revisão',
  'admin.import.review_modal_body':
    'Faltam {n} itens sem marcação (os outros já foram enviados com seus comentários). Esses restantes estão corretos como lidos?',
  'admin.import.review_modal_all_ok': 'Sim, estão corretos',
  'admin.import.review_modal_keep': 'Quero continuar revisando',
  'admin.import.review_applied_toast':
    'Revisão aplicada: {applied} ajuste(s), {removed} removido(s).',
  'admin.import.review_pending_warn': 'Há itens sem revisão — o assistente vai pedir confirmação.',
  'admin.import.nav': 'Importar Menu',
  'admin.import.title': 'Importar Menu',
  'admin.import.subtitle':
    'Envie fotos das páginas ou um PDF — a leitura do menu fica por conta da Pratzu.',
  'admin.import.drop_hint': 'Arraste fotos ou um PDF aqui, ou toque para escolher',
  'admin.import.pages': 'página(s)',
  'admin.import.send': 'Enviar e ler menu',
  'admin.import.processing': 'Lendo o menu…',
  'admin.import.page_progress': 'Página {n} de {total}',
  'admin.import.review_title': 'Revisar importação',
  'admin.import.language': 'Idioma do Menu',
  'admin.import.confirm_language': 'Confirme o idioma detectado',
  // #299 (PR-2) + #474 (revisão UX): preço não reconhecido (0,00/PSM) —
  // produto/variante vão pausados até o dono pôr o preço na revisão
  'admin.import.price_confirm_badge': 'Confirmar preço',
  'admin.import.price_original': 'Preço impresso no Menu: {raw}',
  'admin.import.price_confirm_hint': 'Sem preço, o prato fica pausado e não aparece no Menu.',
  'admin.import.price_confirm_hint_option': 'Sem preço, esta opção não aparece no Menu.',
  'admin.import.price_still_missing':
    'O preço deste prato não foi alterado. Escreva-o no campo de preço.',
  'admin.import.price_of': 'Preço de {name}',
  'admin.import.price_base_from_variants': 'Preço base = o mais baixo das opções com preço',
  'admin.import.price_filter': 'Por confirmar',
  'admin.import.price_invalid': 'Preço inválido.',
  'admin.import.nothing_to_confirm': 'Nada por confirmar.',
  'admin.import.see_all': 'Ver todos',
  'admin.import.paused_dishes_no_price': 'Pratos pausados por falta de preço: {n}',
  'admin.import.paused_options_no_price': 'Opções pausadas por falta de preço: {n}',
  'admin.import.detected': 'detectado',
  'admin.import.restaurant_info': 'Dados do restaurante',
  'admin.import.apply_info': 'Aplicar nome, sobre e logo no restaurante',
  'admin.import.categories': 'Categorias',
  'admin.import.products_count': 'produto(s)',
  'admin.import.extras': 'Extras',
  'admin.import.variants': 'Variações',
  'admin.import.ingredients': 'Ingredientes',
  'admin.import.has_photo': 'tem foto no menu',
  'admin.import.remove': 'Remover',
  'admin.import.mode': 'Ao importar',
  'admin.import.append': 'Adicionar ao Menu atual',
  'admin.import.replace': 'Substituir o Menu atual',
  'admin.import.replace_warn': 'Os itens do Menu atual serão desativados.',
  'admin.import.commit': 'Importar Menu',
  'admin.import.committing': 'Importando…',
  'admin.import.done_title': 'Menu importado!',
  'admin.import.done_desc': 'categorias e produtos criados no idioma do menu.',
  'admin.import.error': 'Não foi possível ler o menu. Tente novamente.',
  'admin.import.cancel': 'Cancelar importação',
  'admin.import.no_items': 'Nenhum item reconhecido — confira as fotos.',
  'admin.import.name': 'Nome',
  'admin.import.description': 'Descrição',
  'admin.import.price': 'Preço',
  'admin.import.open_menu': 'Abrir menu',
  'admin.import.chat_placeholder': 'Responda o assistente…',
  'admin.import.send_photo': 'Enviar foto',
  'admin.import.review_cta': 'Revisar e importar',
  'admin.import.replaced_page': 'Foto da página {n} substituída — relendo…',
  'admin.import.start': 'Lendo o menu…',
  'admin.branding.pay_title': 'Pagamentos',
  'admin.branding.pay_hint': 'Formas de pagamento oferecidas na hora de fechar a conta na mesa.',
  'admin.branding.pay_cash': 'Dinheiro',
  'admin.branding.pay_card': 'Cartão no local',
  'admin.branding.pay_saved': 'Formas de pagamento atualizadas.',
  'admin.branding.tz_title': 'Fuso horário',
  'admin.branding.tz_desc':
    'Fuso do restaurante: os horários de funcionamento, promoções e prato do dia seguem este relógio.',
  'admin.branding.tz_label': 'Fuso horário (IANA)',
  'admin.branding.tz_now': 'Agora no restaurante:',
  'admin.branding.tz_save': 'Guardar fuso horário',
  'admin.branding.tz_saved': 'Fuso horário atualizado.',
  'admin.branding.hours_saved_toast': 'Horários guardados!',
  'admin.branding.hours_title': 'Horários de funcionamento',
  'admin.branding.hours.day_0': 'Domingo',
  'admin.branding.hours.day_1': 'Segunda',
  'admin.branding.hours.day_2': 'Terça',
  'admin.branding.hours.day_3': 'Quarta',
  'admin.branding.hours.day_4': 'Quinta',
  'admin.branding.hours.day_5': 'Sexta',
  'admin.branding.hours.day_6': 'Sábado',
  'admin.branding.hours.until': 'até',
  'admin.branding.hours.closed': 'Fechado',
  'admin.branding.hours_save': 'Guardar horários',
  'admin.branding.hours_incomplete':
    'Falta a abertura ou o fechamento em: {days}. Preencha os dois horários ou deixe o dia vazio.',

  // ── combos ──
  'admin.combos.subtitle': 'Agrupe produtos com desconto',
  'admin.combos.new': '+ Novo combo',
  'admin.combos.empty': 'Nenhum combo ainda',
  'admin.combos.empty_hint': 'Agrupe 2 ou mais produtos e ofereça um desconto',
  'admin.combos.create': 'Criar combo',
  'admin.combos.create_short': 'Criar',
  'admin.combos.deactivate': 'Desativar',
  'admin.combos.deactivated_toast': 'Combo desativado',
  'admin.combos.updated_toast': 'Combo atualizado!',
  'admin.combos.created_toast': 'Combo criado!',
  'admin.combos.edit_title': 'Editar combo',
  'admin.combos.new_title': 'Novo combo',
  'admin.combos.name': 'Nome do combo',
  'admin.combos.discount_type': 'Tipo de desconto',
  'admin.combos.discount_percent': 'Percentual (%)',
  'admin.combos.discount_fixed': 'Valor fixo',
  'admin.combos.discount_value_percent': 'Desconto (%)',
  'admin.combos.discount_value_fixed': 'Desconto',
  'admin.combos.items': 'Produtos do combo (mín. 2)',
  'admin.combos.add_product': 'Adicionar produto…',
  'admin.combos.items_min_hint': 'Selecione pelo menos 2 produtos',

  // ── impostos ──
  'admin.taxes.title': 'Regras de Imposto',
  'admin.taxes.subtitle': 'Configure impostos por categoria de produto',
  'admin.taxes.new': '+ Nova regra',
  'admin.taxes.empty': 'Nenhuma regra cadastrada',
  'admin.taxes.empty_hint': 'Configure os impostos aplicáveis por categoria de produto',
  'admin.taxes.create': 'Criar regra',
  'admin.taxes.create_short': 'Criar',
  'admin.taxes.active': 'Ativa',
  'admin.taxes.inactive': 'Inativa',
  'admin.taxes.inclusive': 'incluso no preço',
  'admin.taxes.exclusive': 'sobre o preço',
  'admin.taxes.updated_toast': 'Regra atualizada!',
  'admin.taxes.created_toast': 'Regra criada!',
  'admin.taxes.edit_title': 'Editar regra de imposto',
  'admin.taxes.new_title': 'Nova regra de imposto',
  'admin.taxes.category': 'Categoria de produto',
  'admin.taxes.cat_food': 'Alimentação (food)',
  'admin.taxes.cat_beverage': 'Bebida (beverage)',
  'admin.taxes.cat_tobacco': 'Tabaco (tobacco)',
  'admin.taxes.cat_other': 'Outro',
  'admin.taxes.rate': 'Taxa (%)',
  'admin.taxes.rate_hint': 'Ex.: 13 para 13%, 6.5 para 6,5%',
  'admin.taxes.label': 'Rótulo',
  'admin.taxes.label_ph': 'IVA 13%',
  'admin.taxes.inclusive_label': 'Incluso no preço',
  'admin.taxes.inclusive_hint': 'Imposto já está no valor exibido ao cliente',

  // ── usuários do restaurante ──
  'admin.users.title': 'Usuários do restaurante',
  'admin.users.invite_new': '+ Convidar',
  'admin.users.empty': 'Nenhum usuário',
  'admin.users.empty_hint': 'Convide o primeiro usuário para este restaurante.',
  'admin.users.invite': 'Convidar',
  'admin.users.inviting': 'Convidando…',
  'admin.users.invited_toast': 'Usuário convidado com sucesso',
  'admin.users.invite_err': 'Erro ao convidar usuário',
  'admin.users.invite_title': 'Convidar usuário',
  'admin.users.invite_title_named': 'Convidar usuário — {name}',
  'admin.users.roles_hint': 'Funções (pode marcar mais de uma)',
  'admin.users.roles_required': 'Marque pelo menos uma função',
  'admin.users.role_owner': 'Dono',
  // ── Gerente + ajustes de preço com aprovação (#284 / #274) ──
  'admin.nav.adjustments': 'Ajustes de preço',
  'admin.users.role_manager': 'Gerente',
  'admin.plan.managers': 'Gerentes',
  'admin.plans.col_managers': 'Gerentes',
  'admin.plans.managers_hint': '0 = sem perfil Gerente; vazio = ilimitado',
  'admin.adjust.button_request': 'Pedir ajuste',
  'admin.adjust.button_apply': 'Ajustar preço',
  'admin.adjust.title_request': 'Pedir ajuste de preço',
  'admin.adjust.title_apply': 'Ajustar preço',
  'admin.adjust.needs_approval': 'O ajuste só é aplicado depois que um Gerente ou o Dono aprovar.',
  'admin.adjust.target': 'Aplicar em',
  'admin.adjust.target_order': 'Total do pedido',
  'admin.adjust.target_item': 'Prato',
  'admin.adjust.type': 'Tipo de ajuste',
  'admin.adjust.type_discount_percent': 'Desconto em %',
  'admin.adjust.type_discount_amount': 'Desconto em valor',
  'admin.adjust.type_price_override': 'Preço manual',
  'admin.adjust.type_none': 'Remover ajuste',
  'admin.adjust.value_percent': 'Percentual (%)',
  'admin.adjust.value_money': 'Valor',
  'admin.adjust.value_required': 'Informe um valor maior que zero',
  'admin.adjust.reason': 'Motivo',
  'admin.adjust.reason_optional': 'Motivo (opcional)',
  'admin.adjust.reason_required': 'Informe o motivo',
  'admin.adjust.request': 'Enviar pedido',
  'admin.adjust.apply': 'Aplicar',
  'admin.adjust.requested_toast': 'Pedido de ajuste enviado para aprovação',
  'admin.users.seats': 'Vagas do plano por perfil',
  'admin.users.edit_roles': 'Perfis',
  'admin.users.edit_roles_title': 'Perfis de {email}',
  'admin.users.roles_seat_hint':
    'Uma pessoa pode ter vários perfis ao mesmo tempo; cada perfil ocupa uma vaga do limite do plano.',
  'admin.users.roles_saved_toast': 'Perfis atualizados',
  'admin.users.roles_err': 'Erro ao guardar os perfis',
  'admin.waiter_limit.title': 'Limite do garçom para ajustes de preço',
  'admin.waiter_limit.desc':
    'Descontos e acréscimos do garçom até este limite valem na hora (com auditoria). Acima do limite — ou sem limite — viram pedido de aprovação para o Gerente ou o Dono.',
  'admin.waiter_limit.mode': 'Tipo de limite',
  'admin.waiter_limit.mode_none': 'Sem limite (tudo precisa de aprovação)',
  'admin.waiter_limit.mode_percent': 'Percentual do preço',
  'admin.waiter_limit.mode_amount': 'Valor fixo',
  'admin.waiter_limit.value_percent': 'Limite (%)',
  'admin.waiter_limit.value_amount': 'Limite (valor por ajuste)',
  'admin.waiter_limit.save': 'Guardar limite',
  'admin.waiter_limit.invalid':
    'Limite inválido: use % de 0 a 100 ou um valor maior ou igual a zero.',
  'admin.adjust.auto_applied_toast': 'Ajuste aplicado (dentro do seu limite)',
  'admin.adjust.auto_approved_badge': 'Auto-aprovado (dentro do limite)',
  'admin.adjust.applied_toast': 'Ajuste aplicado',
  'admin.adjust.new_request_toast': 'Novo pedido de ajuste — mesa {table}',
  'admin.adjust.your_approved_toast': 'Seu pedido de ajuste foi aprovado',
  'admin.adjust.your_rejected_toast': 'Seu pedido de ajuste foi recusado: {note}',
  'admin.adjust.page_title': 'Ajustes de preço',
  'admin.adjust.page_hint': 'Aprove ou recuse os pedidos de ajuste da equipe',
  'admin.adjust.tab_pending': 'Pendentes',
  'admin.adjust.tab_audit': 'Auditoria',
  'admin.adjust.empty': 'Nenhum pedido pendente',
  'admin.adjust.empty_hint': 'Os pedidos de ajuste dos garçons aparecem aqui em tempo real.',
  'admin.adjust.approve': 'Aprovar',
  'admin.adjust.reject': 'Recusar',
  'admin.adjust.reject_title': 'Recusar pedido de ajuste',
  'admin.adjust.reject_reason': 'Motivo da recusa',
  'admin.adjust.approved_toast': 'Ajuste aprovado e aplicado',
  'admin.adjust.rejected_toast': 'Pedido de ajuste recusado',
  'admin.adjust.requested_by': 'Pedido por {name}',
  'admin.adjust.decided_by': 'Decidido por {name}',
  'admin.adjust.rule_none': 'sem ajuste',
  'admin.adjust.audit_applied': 'Ajustes aplicados',
  'admin.adjust.audit_requests': 'Pedidos recusados, cancelados ou pendentes',
  'admin.adjust.col_when': 'Quando',
  'admin.adjust.col_change': 'Ajuste',
  'admin.adjust.col_total': 'Total do pedido',
  'admin.adjust.col_requested': 'Pedido por',
  'admin.adjust.col_decided': 'Aplicado por',
  'admin.adjust.col_reason': 'Motivo',
  'admin.adjust.status_pending': 'Pendente',
  'admin.adjust.status_approved': 'Aprovado',
  'admin.adjust.status_rejected': 'Recusado',
  'admin.adjust.status_cancelled': 'Cancelado',
  'admin.users.role_kitchen': 'Cozinha',
  'admin.users.role_waiter': 'Garçom',
  'admin.users.role_customer': 'Cliente',
  'admin.users.email': 'Email',
  'admin.users.email_ph': 'usuario@exemplo.com',
  'admin.users.name': 'Nome',
  'admin.users.name_optional': 'Nome (opcional)',
  'admin.users.name_ph': 'Ex: Maria Santos',
  'admin.users.role': 'Função',
  'admin.users.status': 'Estado',
  'admin.users.active': 'Ativo',
  'admin.users.inactive': 'Inativo',
  'admin.users.deactivate': 'Desativar',
  'admin.users.deactivate_confirm': 'Desativar {email}?',
  'admin.users.deactivated_toast': 'Usuário desativado',
  'admin.users.deactivate_err': 'Erro ao desativar usuário',
  'admin.users.reactivate': 'Reativar',
  'admin.users.reactivate_confirm': 'Reativar {email}?',
  'admin.users.reactivated_toast': 'Usuário reativado',
  'admin.users.reactivate_err': 'Erro ao reativar usuário',
  'admin.users.load_err': 'Erro ao carregar usuários',

  // ── sessão da plataforma (#430) ──
  'admin.impersonation.banner': 'Sessão da plataforma em {restaurant} · termina às {time}',
  'admin.impersonation.banner_soon': 'Faltam 5 minutos · termina às {time}',
  'admin.impersonation.banner_reason': 'Motivo: {reason}',
  'admin.impersonation.end': 'Terminar',
  'admin.impersonation.end_aria': 'Terminar a sessão da plataforma em {restaurant}',
  'admin.impersonation.end_err': 'Não foi possível terminar a sessão. Tente de novo.',
  'admin.impersonation.expired': 'A sessão da plataforma terminou.',
  'admin.impersonation.expired_notice':
    'A sessão da plataforma em {restaurant} terminou às {time}.',
  'admin.impersonation.enter_again': 'Entrar de novo',
  'admin.impersonation.switch_confirm': 'Terminar a sessão em {restaurant}?',
  'admin.impersonation.start_title': 'Entrar em {restaurant} como plataforma',
  'admin.impersonation.reason_label': 'Motivo',
  'admin.impersonation.reason_hint': 'O restaurante vê este motivo na lista de acessos.',
  'admin.impersonation.reason_required': 'Informe o motivo.',
  'admin.impersonation.reason_length': 'O motivo precisa de 3 a 500 caracteres.',
  'admin.impersonation.start': 'Entrar',
  'admin.impersonation.start_err': 'Não foi possível iniciar a sessão. Tente de novo.',
  'admin.impersonation.err_already':
    'Já tem uma sessão da plataforma aberta. Termine-a antes de entrar em outro restaurante.',
  'admin.impersonation.err_inactive': 'Este restaurante está inativo.',
  'admin.impersonation.access_title': 'Acessos da equipe da Pratzu',
  'admin.impersonation.access_who': 'Equipe da Pratzu',
  'admin.impersonation.access_empty': 'Sem acessos da equipe da Pratzu.',
  'admin.impersonation.access_err': 'Não foi possível carregar os acessos.',
  'admin.impersonation.access_retry': 'Tentar de novo',
  'admin.impersonation.access_more': 'Ver mais',
  'admin.impersonation.access_expired': '{duration} · tempo esgotado',
  'admin.impersonation.access_active': 'Em curso',
  'admin.impersonation.access_col_when': 'Data e hora',
  'admin.impersonation.access_col_duration': 'Duração',
  'admin.impersonation.access_col_who': 'Quem',
  'admin.impersonation.access_col_reason': 'Motivo',

  // ── usuários da plataforma ──
  'admin.platform.users_title': 'Usuários da plataforma',
  'admin.platform.users_subtitle': 'Platform owners com acesso total à plataforma',
  'admin.platform.users_invite_new': '+ Convidar',
  'admin.platform.users_empty': 'Nenhum usuário',
  'admin.platform.users_empty_hint': 'Convide o primeiro administrador da plataforma.',
  'admin.platform.users_invite': 'Convidar',
  'admin.platform.users_inviting': 'Convidando…',
  'admin.platform.users_invited_toast': 'Usuário convidado com sucesso',
  'admin.platform.users_invite_err': 'Erro ao convidar usuário',
  'admin.platform.users_invite_title': 'Convidar platform owner',
  'admin.platform.users_invite_hint':
    'O usuário receberá acesso total à plataforma. Pode entrar com OTP pelo email indicado.',
  'admin.platform.users_email': 'Email',
  'admin.platform.users_email_ph': 'admin@exemplo.com',
  'admin.platform.users_name': 'Nome',
  'admin.platform.users_name_optional': 'Nome (opcional)',
  'admin.platform.users_name_ph': 'Ex: João Silva',
  'admin.platform.users_status': 'Estado',
  'admin.platform.users_active': 'Ativo',
  'admin.platform.users_inactive': 'Inativo',
  'admin.platform.users_deactivate': 'Desativar',
  'admin.platform.users_deactivate_confirm': 'Desativar {email}?',
  'admin.platform.users_deactivated_toast': 'Usuário desativado',
  'admin.platform.users_deactivate_err': 'Erro ao desativar usuário',
  'admin.platform.users_reactivate': 'Reativar',
  'admin.platform.users_reactivate_confirm': 'Reativar {email}?',
  'admin.platform.users_reactivated_toast': 'Usuário reativado',
  'admin.platform.users_reactivate_err': 'Erro ao reativar usuário',
  'admin.features.ai_translation': 'Tradução sugerida',
  'admin.features.menu_import_ocr': 'Importar menu (OCR)',
  'admin.features.promotions': 'Promoções',
  'admin.features.updated_toast': 'Feature atualizada.',
  'admin.features.enabled_hint': 'Ativa — clique para desativar',
  'admin.features.disabled_hint': 'Desativada — clique para ativar',
  'admin.nav.platform_plans': 'Bundles',
  'admin.plans.title': 'Bundles (planos de contratação)',
  'admin.plans.subtitle': 'Preços, limites e integração com o Stripe de cada plano.',
  'admin.plans.load_err': 'Erro ao carregar planos.',
  'admin.plans.save_err': 'Erro ao guardar plano.',
  'admin.plans.saved_toast': 'Plano atualizado.',
  'admin.plans.empty': 'Nenhum plano configurado.',
  'admin.plans.empty_hint': 'Verifique a migração de billing (0040).',
  'admin.plans.col_plan': 'Plano',
  'admin.plans.col_price': 'Preço',
  'admin.plans.col_kitchen': 'Cozinheiros',
  'admin.plans.col_waiters': 'Garçons',
  'admin.plans.col_translations': 'Idiomas de tradução',
  'admin.plans.col_stripe': 'Stripe price',
  'admin.plans.col_status': 'Status',
  'admin.plans.edit': 'Editar',
  'admin.plans.edit_title': 'Editar plano {name}',
  'admin.plans.description': 'Descrição',
  'admin.plans.price_cents': 'Preço (centavos/mês)',
  'admin.plans.currency': 'Moeda',
  'admin.plans.stripe_price_id': 'Stripe price ID',
  'admin.plans.unlimited': '∞',
  'admin.plans.all_languages': 'Todos',
  'admin.plans.free': 'Grátis',
  'admin.plans.no_stripe': 'não configurado',
  'admin.plans.active': 'Ativo',
  'admin.plans.inactive': 'Inativo',
  'admin.plans.saving': 'Guardando…',
  'admin.plans.save': 'Guardar',
  'admin.plan.current': 'Plano atual',
  'admin.plan.per_month': 'mês',
  'admin.plan.kitchen': 'Cozinheiros',
  'admin.plan.waiters': 'Garçons',
  'admin.plan.translations': 'Traduções',
  'admin.plan.upgrade_sb': 'Contratar Small Business',
  'admin.plan.upgrade_pro': 'Contratar Pro',
  'admin.plan.manage': 'Gerenciar assinatura',
  'admin.plan.checkout_err': 'Não foi possível iniciar o checkout.',
  'admin.plan.portal_err': 'Não foi possível abrir o portal de assinatura.',
  'admin.import.outcome_title': 'Menu sendo montado',
  'admin.import.outcome_hint': 'Enquanto conversamos, a leitura das páginas aparece aqui.',
  'admin.import.outcome_hint_live':
    'Isso pode levar de dezenas de segundos a alguns minutos — o processamento continua em segundo plano; esta página atualiza sozinha.',
  'admin.import.phase_reading': 'Lendo {n} página(s) em paralelo…',
  'admin.import.phase_analyzing': 'Montando o menu e checando compatibilidade…',
  'admin.import.phase_reviewing': 'Aplicando seus ajustes…',
  'admin.import.page_chip': 'pág. {n}',
  'admin.import.page_pending': 'Na fila / sendo lida',
  'admin.import.page_ocr_done': 'Lida',
  'admin.import.page_error': 'Falhou',
  'admin.import.tab_draft': 'Rascunho',
  'admin.import.tab_review': 'Revisão detalhada',
  'admin.import.flags_title': '{n} item(ns) precisam de atenção',
  'admin.import.flags_hint': 'Cada decisão é aplicada no rascunho na hora e o aviso some.',
  'admin.import.flag_keep_extra': 'Manter "{extra}"',
  'admin.import.flag_remove_extra': 'Remover "{extra}"',
  'admin.import.flag_confirm_item': 'Está correto',
  'admin.import.flag_fix_item': 'Precisa de ajuste',
  'admin.import.flags_apply_all': 'Aplicar sugestões',
  'admin.import.flags_keep_as_printed': 'Manter como impresso',
  // #403 (spec final do painel de avisos): as duas opções são aceitações —
  // documento = manter o texto impresso; setas de troca = mudar para a
  // sugestão. flag_confirm_item/flag_fix_item/flags_apply_all são
  // chaves ANTIGAS já semeadas (guarda banco ⊆ fonte) — apenas sem uso aqui.
  'admin.import.flags_keep_all_printed': 'Manter todas como impresso',
  'admin.import.flags_switch_all': 'Mudar todas para a sugestão',
  'admin.import.flag_switch': 'Mudar para a sugestão',
  'admin.import.flag_keep_printed': 'Manter como impresso',
  'admin.import.flag_resolved_toast': '{n} aviso(s) resolvido(s).',
  'admin.import.link_title': 'Menu digital por link',
  'admin.import.link_button': 'Importar link',
  'admin.import.link_hint':
    'Cole o endereço do menu online (site, página ou PDF publicado). Aplicativos que só abrem no celular não trazem o menu no link — use fotos.',
  'admin.import.step_ocr': 'Ler as páginas',
  'admin.import.step_agent': 'Entender o menu',
  'admin.import.step_semantic': 'Validar a leitura',
  'admin.import.step_done': 'Rascunho pronto',
  'admin.import.fix_progress': '{done} de {total} ajustes aplicados',
  'admin.import.kick': 'Forçar retomada',
  'admin.import.kick_done': 'Retomada forçada — o processo continua em segundo plano.',
  'admin.import.kick_stale_hint': 'Sem progresso há {s}s — force a retomada.',
  'admin.import.awaiting_title': 'Aguardando sua resposta',
  'admin.import.awaiting_photo_fallback':
    'A página {n} não foi lida direito. Envie uma nova foto dela ou siga sem ela.',
  'admin.import.awaiting_input_fallback':
    'O assistente precisa de uma resposta sua para continuar. Responda no chat ou siga sem responder.',
  'admin.import.awaiting_send_photo': 'Enviar nova foto',
  'admin.import.awaiting_skip': 'Seguir sem esta página',
  'admin.import.awaiting_continue': 'Seguir sem responder',
  'admin.import.awaiting_cancel': 'Cancelar importação',
  'admin.import.awaiting_hint': 'O processo está pausado esperando você — não está travado.',
  // #491 (PR-2): secção «Continue de onde parou» — importações retomáveis na
  // fase 1 do import (texto da orientação UX; {date}/{pages} interpolados no
  // componente com <bdi> para escritas RTL). resume_dishes fica semeada para o
  // follow-up («Pratos lidos»), sem uso no componente na v1.
  'admin.import.resume_title': 'Continue de onde parou',
  'admin.import.resume_cta': 'Continuar importação',
  'admin.import.resume_cta_aria': 'Continuar importação de {date}',
  'admin.import.resume_meta': '{source} · Páginas: {pages}',
  'admin.import.resume_dishes': 'Pratos lidos: {n}',
  'admin.import.resume_source_photos': 'Fotos ou PDF',
  'admin.import.resume_source_link': 'Link',
  'admin.import.resume_status_ready': 'Pronta para revisar',
  'admin.import.resume_status_working': 'Em andamento',
  'admin.import.start_new': 'Ou comece uma importação nova',
  'admin.import.start_new_hint':
    'Uma importação nova lê o Menu outra vez e não leva as correções já feitas.',
  // #299 crit. 9: rótulo próprio distingue o idioma do Menu do do restaurante
  'admin.import.menu_locale_label': 'Idioma do Menu',
  'admin.import.restaurant_locale_label': 'Idioma do restaurante',
  // #299 crit. 7 (DECISÃO B, textos UX 6392564531): aviso bloqueante com as
  // duas saídas — (a) confirmar a moeda do restaurante; (b) fale com a equipe
  'admin.import.currency_mismatch':
    'Os preços deste Menu foram lidos em {detected}, mas o restaurante usa {restaurant}. A Pratzu não converte preços. Se os preços estão mesmo em {restaurant}, use o botão abaixo. Se estão em {detected}, fale com a equipe da Pratzu para mudar a moeda do restaurante antes da importação.',
  'admin.import.currency_mismatch_deduced':
    'O Menu está em {menu} e não mostra a moeda; a Pratzu deduziu {detected}, mas o restaurante usa {restaurant}. A Pratzu não converte preços. Se os preços estão mesmo em {restaurant}, use o botão abaixo. Se estão em {detected}, fale com a equipe da Pratzu para mudar a moeda do restaurante antes da importação.',
  'admin.import.currency_confirm_restaurant': 'Os preços estão em {currency}',
  'admin.import.currency_restaurant_missing':
    'O restaurante ainda não tem país nem moeda definidos. Fale com a equipe da Pratzu para os definir antes de importar este Menu.',
  'admin.import.currency_confirm_to_import': 'Resolva a moeda para importar',
  'admin.import.currency_confirmed_line': 'Moeda confirmada: {currency} ✓',
  'admin.import.currency_line':
    'Moeda lida no Menu: {detected} · Moeda do restaurante: {restaurant}',
  'admin.import.currency_line_deduced':
    'O Menu não mostra a moeda; o restaurante usa {restaurant}.',
  // (UX) 422 de moeda/idioma mostrados na língua do painel
  'admin.import.error_currency_not_restaurant':
    'Só é possível confirmar a moeda do restaurante ({restaurant}).',
  'admin.import.error_currency_invalid': 'Moeda não suportada.',
  'admin.import.error_locale_confirmation': 'Confirme o idioma do Menu antes de importar.',
  'admin.import.locale_translations_note':
    'Os pratos entram em {menu}; as traduções para {restaurant} são feitas depois.',
  'admin.import.service_per_person': 'pessoa',
  'admin.import.service_per_bill': 'conta',
  'admin.import.awaiting_photo_page': 'Foto da página',
  'admin.import.photo_request_failed':
    'Não consegui ler a página {n} (a leitura falhou). Envie uma nova foto dela pelo botão 📷 ou toque em "{skip}".',
  'admin.import.photo_request_poor':
    'A página {n} ficou difícil de ler. Envie uma nova foto dela pelo botão 📷 ou toque em "{skip}".',
  'admin.import.needs_attention': 'Precisa de atenção',
  'admin.import.chat_done_hint': 'Conversa concluída — ajustes pela aba de revisão.',
  'admin.platform.users_load_err': 'Erro ao carregar usuários',

  // ── traduções do restaurante (#336) — página própria, fora do Branding ──
  'admin.translations.title': 'Traduções',
  'admin.translations.subtitle': 'Idiomas do menu e traduções sugeridas',

  // ── traduções da plataforma ──
  'admin.translations.coverage_title': 'Cobertura do dicionário por idioma',
  'admin.translations.coverage_desc':
    'Textos de interface (painel, menus e página de login) traduzidos em cada idioma do catálogo — fonte: português.',
  'admin.translations.coverage_complete': 'Completo',
  'admin.translations.coverage_missing': 'Faltam {count}',
  'admin.translations.base_title': 'Traduções da base (interface)',
  'admin.translations.base_desc':
    'Textos de interface compartilhados por todos os menus (botões, carrinho, checkout…). Fonte: tabela ui_texts — {count} base(s) de idioma no catálogo.',
  'admin.translations.base_refresh': 'Iniciar/Atualizar traduções da base',
  'admin.translations.base_force': 'Forçar re-tradução (refaz todos os rascunhos)',
  'admin.translations.base_hint':
    '"Iniciar/Atualizar" preenche os idiomas do catálogo que ainda não têm dicionário (1 tradução sugerida por idioma). "Forçar" descarta antes os rascunhos e re-traduz — textos revisados manualmente nunca são alterados.',
  'admin.translations.per_tenant_title': 'Traduções por restaurante',
  'admin.translations.per_tenant_desc':
    'Re-traduz o menu completo (produtos, categorias, ingredientes, variações, adicionais e descrição) do restaurante escolhido, a partir do idioma primário.',
  'admin.translations.empty_title': 'Nenhum restaurante',
  'admin.translations.empty_hint': 'Crie um restaurante para gerenciar traduções.',
  'admin.translations.primary_locale': 'idioma principal {locale}',
  'admin.translations.update_button': 'Atualizar traduções',
  'admin.translations.runs_title': 'Últimas execuções',
  'admin.translations.runs_desc':
    'Base = dicionário da interface (plataforma) · Menu = conteúdo de um restaurante. Clique em Logs para ver os eventos de cada execução.',
  'admin.translations.base_started_toast':
    'Traduções da base iniciadas — {count} idioma(s): {bases}',
  'admin.translations.tenant_started_toast':
    '{name}: re-tradução iniciada — {count} rascunho(s) regenerados',
  'admin.translations.tenant_fallback': 'restaurante',

  // ── restaurantes (plataforma) ──
  'admin.tenant.new': 'Novo restaurante',
  'admin.tenant.create': 'Criar restaurante',
  'admin.tenant.creating': 'Criando…',
  'admin.tenant.created_ok': 'Restaurante criado com sucesso',
  'admin.tenant.created_err': 'Não foi possível criar o restaurante. Tente de novo.',
  'admin.tenant.name': 'Nome do restaurante',
  'admin.tenant.name_ph': 'Ex: Pizzaria Roma',
  'admin.tenant.slug': 'Endereço do menu',
  'admin.tenant.slug_ph': 'Ex: pizzaria-roma',
  'admin.tenant.slug_hint': 'Letras minúsculas, números e hífens (3 a 40).',
  'admin.tenant.slug_preview': 'pratzu.com/r/{slug}',
  'admin.tenant.slug_err_reserved': 'Este endereço está reservado.',
  'admin.tenant.slug_err_format':
    'Use 3 a 40 letras minúsculas, números ou hífens, sem hífen no início nem no fim.',
  'admin.tenant.slug_err_too_long': 'Use no máximo 40 caracteres.',
  'admin.tenant.slug_err_taken': 'Este endereço já está em uso.',
  'admin.tenant.slug_use_suggestion': 'Usar {suggestion}',
  'admin.tenant.country': 'País',
  'admin.tenant.locale': 'Idioma',
  'admin.tenant.owner_email': 'E-mail do dono (opcional)',
  'admin.tenant.owner_email_ph': 'dono@restaurante.com',
  'admin.tenant.owner_email_hint': 'Esta pessoa recebe acesso como dono.',
  'admin.tenant.name_required': 'Informe o nome do restaurante.',
  'admin.tenant.owner_email_invalid': 'Informe um e-mail válido.',

  // ── ideias do concierge (página de plataforma) ──
  'admin.ideas.nav': 'Ideias do concierge',
  'admin.ideas.title': 'Ideias do concierge',
  'admin.ideas.desc': 'Conversas do chat da página da Pratzu — o que as pessoas querem facilitar.',
  'admin.ideas.empty_title': 'Nenhuma conversa ainda',
  'admin.ideas.empty_hint': 'As conversas do chat institucional aparecem aqui com resumo e tags.',
  'admin.ideas.no_summary': 'Sem resumo (conversa não concluída)',
  'admin.ideas.messages': 'Mensagens',
  'admin.ideas.show': 'Ver conversa',
  'admin.ideas.hide': 'Fechar',

  // ── mensagens de contato (página de plataforma) ──
  'admin.contact.nav': 'Mensagens de contato',
  'admin.countries.nav': 'Países',
  'admin.countries.title': 'Países',
  'admin.countries.desc':
    'Cadastro do Menu multi-idioma e da versão completa (pedidos, POS e caixa) por país.',
  'admin.countries.summary':
    'Versão completa homologada em: {homologated}. Cadastro fechado em: {closed}.',
  'admin.countries.search': 'Buscar país (nome ou código)',
  'admin.countries.only_exceptions': 'Só países homologados ou fechados',
  'admin.countries.country': 'País',
  'admin.countries.signup': 'Cadastro aberto',
  'admin.countries.homologated': 'Versão completa',
  'admin.countries.save_error': 'Não foi possível guardar o país.',
  'admin.billing_cfg.title': 'Teste grátis e cobrança',
  'admin.billing_cfg.desc':
    'Duração do teste grátis (sem cartão), carência para pagamento em atraso e regras do teste.',
  'admin.billing_cfg.trial_days': 'Dias de teste grátis',
  'admin.billing_cfg.past_due_grace_days': 'Dias de carência após atraso no pagamento',
  'admin.billing_cfg.trial_once_per_tenant': 'Um teste grátis por restaurante',
  'admin.billing_cfg.trial_once_per_owner_email': 'Um teste grátis por e-mail do dono',
  'admin.billing_cfg.existing_tenants_new_trial':
    'Restaurantes já cadastrados ganham um novo teste grátis',
  'admin.billing_cfg.save': 'Guardar',
  'admin.billing_cfg.saved': 'Configuração guardada.',
  'admin.billing_cfg.save_error': 'Não foi possível guardar a configuração.',
  'admin.leads.nav': 'Leads',
  'admin.leads.title': 'Leads',
  'admin.leads.desc':
    'Pedidos de mais informações sobre Small Business e Pro e pedidos de aviso quando o cadastro abrir no país.',
  'admin.leads.filter_status': 'Status',
  'admin.leads.filter_plan': 'Plano',
  'admin.leads.filter_country': 'País',
  'admin.leads.all': 'Todos',
  'admin.leads.status_new': 'Novo',
  'admin.leads.status_contacted': 'Contatado',
  'admin.leads.status_closed': 'Encerrado',
  'admin.leads.status_spam': 'Spam',
  'admin.leads.plan_small_business': 'Small Business',
  'admin.leads.plan_pro': 'Pro',
  'admin.leads.plan_notify': 'Avise-me',
  'admin.leads.detected': 'detectado',
  'admin.leads.not_notified': 'e-mail para o comercial não enviado',
  'admin.leads.empty_title': 'Nenhum lead',
  'admin.leads.empty_hint': 'Os pedidos da landing e do painel aparecem aqui.',
  'admin.leads.save_error': 'Não foi possível atualizar o lead.',
  'admin.plan.activate_bundle': 'Assinar o Menu multi-idioma',
  'admin.plan.info_sb': 'Mais informações: Small Business',
  'admin.plan.info_pro': 'Mais informações: Pro',
  'admin.plan.status_trial': 'Teste grátis até {date}: seu menu público está no ar.',
  'admin.plan.status_grace': 'Pagamento em atraso: seu menu público fica no ar até {date}.',
  'admin.plan.status_inactive':
    'Menu público pausado: só você vê a prévia. Assine por {price}/mês para colocá-lo no ar de novo.',
  'admin.plan.status_inactive_noprice':
    'Menu público pausado: só você vê a prévia. Assine o Menu multi-idioma para colocá-lo no ar de novo.',
  'admin.lead_modal.title': 'Mais informações sobre o {plan}',
  'admin.lead_modal.plan_sb': 'Small Business',
  'admin.lead_modal.plan_pro': 'Pro',
  'admin.lead_modal.intro':
    'O preço depende das regras fiscais do seu país. Deixe seu nome e e-mail que a equipe comercial da Pratzu entra em contato.',
  'admin.lead_modal.name': 'Seu nome',
  'admin.lead_modal.email': 'E-mail',
  'admin.lead_modal.country': 'País',
  'admin.lead_modal.consent': 'Aceito ser contatado pela Pratzu sobre este pedido.',
  'admin.lead_modal.consent_note':
    'Guardamos seu nome, e-mail, país, idioma e a data deste aceite só para a equipe comercial responder a este pedido. Para corrigir ou apagar seus dados, escreva para sales@pratzu.com.',
  'admin.lead_modal.send': 'Enviar pedido',
  'admin.lead_modal.close': 'Fechar',
  'admin.lead_modal.sent':
    'Obrigado! A equipe comercial da Pratzu vai responder por e-mail em breve.',
  'admin.lead_modal.unavailable': 'Os pedidos de contato abrem em breve.',
  'admin.lead_modal.turnstile_wait': 'Aguarde um instante: estamos verificando o envio.',
  'admin.lead_modal.turnstile_error': 'Não conseguimos verificar o envio. Tente de novo.',
  'admin.lead_modal.err_required': 'Preencha este campo.',
  'admin.lead_modal.err_email': 'Informe um e-mail válido.',
  'admin.lead_modal.err_invalid': 'Confira este campo.',
  'admin.lead_modal.err_consent': 'Marque para continuar.',
  'admin.lead_modal.err_validation': 'Confira os campos destacados.',
  'admin.lead_modal.err_rate': 'Muitas tentativas. Tente de novo em alguns minutos.',
  'admin.lead_modal.err_generic': 'Não foi possível enviar. Tente de novo.',
  'admin.contact.title': 'Mensagens de contato',
  'admin.contact.desc':
    'Formulário da página da Pratzu — perguntas e pedidos que chegam sem passar pelo e-mail.',
  'admin.contact.empty_title': 'Nenhuma mensagem ainda',
  'admin.contact.empty_hint': 'As mensagens enviadas pelo formulário do site aparecem aqui.',
  'admin.contact.new': 'Nova',
  'admin.contact.handled': 'Tratada',
  'admin.contact.mark_handled': 'Marcar como tratada',
  'admin.contact.reopen': 'Reabrir',
  'admin.contact.toggle_error': 'Não foi possível atualizar a mensagem.',

  // ── gestão do menu (Menu.tsx) ──
  'admin.channel.dine_in': '🍽 Mesa',
  'admin.menu.title': 'Menu',
  'admin.menu.subtitle': 'Gerencie categorias e produtos',
  'admin.menu.add_category': '+ Categoria',
  'admin.menu.add_product': '+ Produto',
  'admin.menu.all': 'Todos',
  'admin.menu.empty_title': 'Nenhum produto ainda',
  'admin.menu.empty_desc': 'Crie o primeiro produto para este menu',
  'admin.menu.create_category_first': '+ Categoria primeiro',
  'admin.menu.available': 'Disponível',
  'admin.menu.unavailable': 'Indisponível',
  'admin.menu.live': 'No ar',
  'admin.menu.paused': 'Pausado',
  'admin.menu.no_price': 'Sem preço',
  // #489 M1 (item 3): aviso ao LIGAR o «No ar» com prato a 0 (ou variante
  // disponível a 0) — textos propostos pelo UX, a fechar com o PO no PR
  'admin.menu.zero_price_title': 'Este prato está sem preço',
  'admin.menu.zero_price_body':
    'No ar, os clientes podem pedir este prato por {price}. Corrija o preço ou confirme que é grátis.',
  'admin.menu.zero_price_option_body':
    'No ar, os clientes podem escolher {name} por {price}. Corrija o preço ou confirme que é grátis.',
  'admin.menu.zero_price_fix': 'Corrigir preço',
  'admin.menu.zero_price_go_live': 'Pôr no ar a {price}',
  'admin.menu.new_category': 'Nova categoria',
  'admin.menu.edit_category': 'Editar categoria',
  'admin.menu.category_updated': 'Categoria atualizada!',
  'admin.menu.category_created': 'Categoria criada!',
  'admin.menu.category_deactivated': 'Categoria desativada',
  'admin.menu.deactivate': 'Desativar',
  'admin.menu.name': 'Nome',
  'admin.menu.sort_order': 'Ordem de exibição',
  'admin.menu.sort_hint': 'Menor número aparece primeiro',
  'admin.menu.new_product': 'Novo produto',
  'admin.menu.edit_product': 'Editar produto',
  'admin.menu.create_product': 'Criar produto',
  // legado (#403): o editor passou a usar o indicador único text_language —
  // chave MANTIDA na fonte porque já está semeada no banco (guarda banco ⊆ fonte)
  'admin.menu.name_locale': 'Nome ({locale})',
  // #403: indicador ÚNICO de idioma para o par Nome/Descrição do editor
  'admin.menu.text_language': 'Idioma do texto: {locale}',
  // #387: produto sem nenhuma linha do idioma primário — campos nascem vazios
  'admin.menu.no_source_translation':
    'Este produto ainda não tem nome em {locale}. Escreva o nome e a descrição no idioma principal do restaurante.',
  'admin.menu.price': 'Preço',
  'admin.menu.price_hint': 'Em {currency}',
  // #479: dica do campo derivado (com variantes) — texto validado pelo UX
  'admin.menu.price_from_variants_hint':
    'Vem das variantes: é o preço mais baixo das que estão disponíveis. Para mudar este preço, edite os preços das variantes.',
  'admin.menu.variant_label': 'Rótulo de variantes',
  'admin.menu.variant_label_ph': 'Ex.: Tamanho, Porção, Peso',
  'admin.menu.variant_label_hint': 'Deixe vazio se o produto não tiver variantes',
  'admin.menu.description': 'Descrição',
  'admin.menu.desc_ph': 'Ingredientes e descrição do prato…',
  'admin.menu.category': 'Categoria',
  'admin.menu.tax_category': 'Categoria fiscal',
  'admin.menu.tax_food': 'Alimentação (food)',
  'admin.menu.tax_beverage': 'Bebida (beverage)',
  'admin.menu.tax_tobacco': 'Tabaco (tobacco)',
  'admin.menu.tax_other': 'Outro',
  'admin.menu.available_in': 'Disponível em',
  'admin.menu.takeaway': '🛍 Take-away',
  'admin.menu.delivery': '🚚 Delivery',
  'admin.menu.product_created': 'Produto criado! Pode agora adicionar ingredientes e extras.',
  'admin.menu.product_updated': 'Produto atualizado!',
  'admin.menu.create_product_first':
    'Crie o produto primeiro para adicionar ingredientes e extras.',
  'admin.menu.variants': 'Variantes',
  'admin.menu.no_variants': 'Nenhuma variante cadastrada',
  'admin.menu.variant_ph': 'Ex.: Pequeno',
  'admin.menu.ingredients': 'Ingredientes',
  'admin.menu.no_ingredients': 'Nenhum ingrediente cadastrado',
  'admin.menu.allergens': 'Alérgenos',
  'admin.menu.allergens_hint':
    'Os 14 alérgenos da UE (Reg. 1169/2011). Há sugestões prontas; só a lista que você confirmar vale como informação do prato.',
  'admin.menu.allergens_unconfirmed': 'A confirmar',
  'admin.menu.allergens_confirmed': 'Confirmados',
  'admin.menu.allergens_from_menu': 'Lido no menu',
  'admin.menu.allergens_from_ai': 'Sugestão',
  'admin.menu.allergens_confirm': 'Confirmar alérgenos',
  'admin.menu.allergens_none': 'Sem alérgenos',
  'admin.menu.allergens_saved': 'Alérgenos confirmados',
  'admin.menu.allergens_pending': 'Alérgenos a confirmar',
  'admin.menu.allergens_unreviewed': 'A revisar',
  'admin.menu.allergens_unreviewed_hint':
    'Nenhum alérgeno sugerido. Revise e confirme — mesmo que o prato não tenha nenhum.',
  'admin.menu.allergens_reopen_note': 'Alterar os ingredientes reabre a confirmação dos alérgenos.',
  'admin.menu.ingredient_suggestions': 'Ingredientes prováveis',
  'admin.menu.ingredient_suggestions_hint': 'Sugestões. Só entram no prato depois de aceitos.',
  'admin.menu.ingredient_accept': 'Aceitar',
  'admin.menu.ingredient_dismiss': 'Descartar',
  'admin.menu.ingredients_pending': 'Ingredientes a confirmar',
  // ── atributos do prato (#214/#216: dietas, picância, tags) ──
  'admin.menu.attributes': 'Atributos',
  'admin.menu.attributes_hint':
    'Dietas, picância e tags de sabor. Sugestões ficam pré-marcadas — só "Confirmar atributos" publica.',
  'admin.menu.attributes_pending': 'Atributos a confirmar',
  'admin.menu.attributes_confirm': 'Confirmar atributos',
  'admin.menu.attributes_saved': 'Atributos confirmados.',
  'admin.menu.attributes_diet': 'Dietas',
  'admin.menu.attributes_spice': 'Picância',
  'admin.menu.attributes_flavor': 'Tags de sabor',
  'admin.menu.attributes_flavor_ph': 'ex.: cremoso',
  'admin.menu.attributes_kids': 'Prato infantil',
  'admin.menu.attributes_signature': 'Prato da casa',
  'admin.menu.attributes_suggest': 'Sugerir',
  'admin.menu.attributes_suggesting': 'Sugerindo…',
  'admin.menu.attributes_ai_hint': 'Sugestão — revise e confirme antes de publicar.',
  'admin.menu.attributes_state_confirmed': 'Atributos: confirmados',
  'admin.menu.attributes_state_pending': 'Atributos: a confirmar',
  'admin.menu.attributes_state_none': 'Atributos: a revisar',
  'admin.menu.spice_0': 'Sem picância',
  'admin.menu.spice_1': '🌶 leve',
  'admin.menu.spice_2': '🌶🌶 média',
  'admin.menu.spice_3': '🌶🌶🌶 forte',
  // ── página «Revisar atributos» (#216) ──
  'admin.nav.review_attributes': 'Revisar atributos',
  'admin.attributes_review.title': 'Revisar atributos',
  'admin.attributes_review.subtitle':
    'Alérgenos, dietas, picância e tags em um só fluxo — 5 s por prato.',
  'admin.attributes_review.progress': '{done} de {total} revisados',
  'admin.attributes_review.empty_title': 'Tudo revisado!',
  'admin.attributes_review.empty_desc': 'Nenhum prato com sugestões pendentes.',
  'admin.attributes_review.open_menu': 'Ir ao menu',
  'admin.attributes_review.skip': 'Pular este',
  'admin.menu.review_attributes_link': 'Revisar atributos ({n})',
  // #209/#216 (0106): prato do dia — mesmos textos das seeds pt/en do banco.
  // Glossário 23/10: «menu» no hint (era «cardápio» na 0106) — a migration de
  // revisão do glossário regenera as linhas pt do banco a partir desta fonte.
  'admin.menu.special': 'Prato do dia',
  'admin.menu.special_hint':
    'Destaque do dia no menu — sem preço especial (preço diferente = promoção). Máx. 2 por dia.',
  'admin.menu.special_today': 'Só hoje',
  'admin.menu.special_weekly': 'Toda semana nos dias',
  'admin.menu.special_range': 'De uma data até outra',
  'admin.menu.special_active': 'É prato do dia',
  'admin.menu.special_saved': 'Prato do dia guardado.',
  'admin.menu.special_limit':
    'Já há 2 pratos do dia nesta janela — desative um antes de destacar outro.',
  'admin.menu.special_shortcut': 'Marcar como prato do dia (só hoje)',
  'admin.import.review_allergens_open': 'Revisar alérgenos',
  'admin.import.review_allergens_undo': 'Desfazer confirmação',
  'admin.import.review_confirm_note':
    'Alérgenos e ingredientes sugeridos entram como «a confirmar». Confirme aqui ou depois, no editor do prato.',
  'admin.menu.removable': 'Removível',
  'admin.menu.fixed': 'Fixo',
  'admin.menu.toggle_removable_aria': 'Alternar removível: {name}',
  'admin.menu.ingredient_ph': 'Nome do ingrediente',
  'admin.menu.extras': 'Extras pagos',
  'admin.menu.no_extras': 'Nenhum extra cadastrado',
  'admin.menu.extra_ph': 'Ex.: Queijo extra',
  'admin.menu.images': 'Imagens',
  'admin.menu.no_images': 'Nenhuma imagem cadastrada',
  'admin.menu.uploading': 'Enviando…',
  'admin.menu.add_image': 'Adicionar imagem',
  'admin.menu.primary_image': 'Imagem principal',
  'admin.menu.remove_image': 'Remover imagem',

  // ── promoção do produto (#205, spec §8.1) ──
  'admin.promo.section': 'Promoção',
  'admin.promo.none': 'Sem promoção',
  'admin.promo.type': 'Tipo de desconto',
  'admin.promo.type_percent': 'Percentual (%)',
  'admin.promo.type_fixed': 'Preço final',
  'admin.promo.value_percent': 'Desconto (%)',
  'admin.promo.value_fixed': 'Preço final do item',
  'admin.promo.variant': 'Variante',
  'admin.promo.variant_required': 'Preço final em produto com variantes exige escolher a variante.',
  'admin.promo.days': 'Dias da semana',
  'admin.promo.start_time': 'Começa',
  'admin.promo.end_time': 'Termina',
  'admin.promo.ends_at': 'Data de fim (opcional)',
  'admin.promo.window_hint': 'Horários no fuso do restaurante ({timezone}).',
  'admin.promo.label': 'Rótulo (opcional)',
  'admin.promo.preview_invalid': 'Ajuste o desconto para ver a prévia do preço.',
  'admin.promo.create': 'Criar promoção',
  'admin.promo.created': 'Promoção criada!',
  'admin.promo.updated': 'Promoção atualizada!',
  'admin.promo.turn_off': 'Desligar',
  'admin.promo.turn_on': 'Ligar',
  'admin.promo.state_percent': 'Promo −{value}%',
  'admin.promo.state_fixed': 'Promo {price}',
  'admin.promo.state_scheduled': 'Agendada',
  'admin.promo.state_expired': 'Expirada',
  'admin.promo.state_off': 'Desligada',
  'admin.promo.shortcut_title': 'Promoção do produto',

  // ── modal de pedido do garçom (OrderFormModal) ──
  'admin.orderForm.edit_title': 'Editar pedido {id}',
  'admin.orderForm.new_title': 'Novo pedido',
  'admin.orderForm.save_changes': 'Guardar alterações',
  'admin.orderForm.submit': 'Enviar pedido',
  'admin.orderForm.table': 'Mesa',
  'admin.orderForm.select_table': 'Selecione a mesa…',
  'admin.orderForm.table_n': 'Mesa {n}',
  'admin.orderForm.need_item': 'Adicione ao menos um item',
  'admin.orderForm.need_table': 'Escolha a mesa',
  'admin.orderForm.need_reference': 'Informe a referência (agrupa os pedidos da mesa na cozinha)',
  'admin.orderForm.updated': 'Pedido atualizado',
  'admin.orderForm.created': 'Pedido criado',
  'admin.orderForm.save_error': 'Erro ao guardar pedido',
  'admin.orderForm.add_items': 'Adicionar itens',
  'admin.orderForm.search_ph': 'Buscar produto…',
  'admin.orderForm.customizable': 'personalizável',
  'admin.orderForm.no_products': 'Nenhum produto encontrado',
  'admin.orderForm.reference': 'Referência *',
  'admin.orderForm.reference_ph': 'Ex.: nome do cliente — agrupa os pedidos na cozinha',
  'admin.orderForm.notes': 'Observações',
  'admin.orderForm.optional': 'Opcional',
  'admin.orderForm.edit_hint':
    'Personalizações dos itens existentes são preservadas na edição. Só é possível editar pedidos antes do preparo começar.',
  'admin.orderForm.variation': 'Variação',
  'admin.orderForm.remove_ingredients': 'Remover ingredientes',
  'admin.orderForm.without': 'sem',
  'admin.orderForm.tap_to_remove': 'Toque para remover do item',
  'admin.orderForm.optionals': 'Opcionais',
  'admin.orderForm.qty': 'Qtd',
  'admin.orderForm.choose_variation': 'Escolha a variação',
  'admin.orderForm.add_price': 'Adicionar — {price}',
}

export const ADMIN_EN: Record<string, string> = {
  'admin.nav.dashboard': 'Home',
  'admin.nav.kitchen': 'Kitchen',
  'admin.nav.waiter': 'Waiter',
  'admin.nav.branding': 'Branding',
  'admin.nav.menu': 'Menu',
  'admin.nav.translations': 'Translations',
  'admin.nav.combos': 'Combos',
  'admin.nav.taxes': 'Taxes',
  'admin.nav.tables': 'Tables',
  'admin.nav.users': 'Users',
  'admin.nav.platform': 'Platform',
  'admin.nav.platform_users': 'Platform users',
  'admin.nav.platform_tenants': 'Restaurants',
  'admin.nav.platform_translations': 'Platform translations',
  'admin.nav.logout': 'Sign out',
  'admin.nav.section.operations': 'Operations',
  'admin.nav.section.management': 'Management',
  'admin.nav.section.platform': 'Platform',
  'admin.nav.language': 'Language',
  'admin.nav.collapse': 'Collapse sidebar',
  'admin.nav.expand': 'Show sidebar',
  'admin.country.pt': 'Portugal',
  'admin.country.br': 'Brazil',
  'admin.country.us': 'United States',
  'admin.country.es': 'Spain',

  'admin.common.save': 'Save',
  'admin.common.create': 'Create',
  'admin.common.saving': 'Saving…',
  'admin.common.cancel': 'Cancel',
  'admin.common.close': 'Close',
  'admin.common.delete': 'Delete',
  'admin.common.edit': 'Edit',
  'admin.common.new': 'New',
  'admin.common.confirm': 'Confirm',
  'admin.common.search': 'Search',
  'admin.common.loading': 'Loading…',
  'admin.common.empty': 'Nothing here yet',
  'admin.common.error': 'Failed to load',
  'admin.common.back': 'Back',
  'admin.common.active': 'Active',
  'admin.common.inactive': 'Inactive',
  'admin.common.yes': 'Yes',
  'admin.common.no': 'No',
  'admin.common.minutes': 'min',

  'admin.status.pending': 'New',
  'admin.status.confirmed': 'Confirmed',
  'admin.status.preparing': 'Preparing',
  'admin.status.ready': 'Ready',
  'admin.status.delivered': 'Delivered',
  'admin.status.cancelled': 'Cancelled',

  'admin.channel.takeaway': '🛍 Take-away',
  'admin.channel.delivery': '🚚 Delivery',

  'admin.orders.table': 'Table',
  'admin.orders.customer': 'Customer',
  'admin.orders.done': 'done',
  'admin.orders.notes': 'notes',
  'admin.orders.without': 'without',
  'admin.orders.entered_at': 'Received',
  'admin.orders.confirm': 'Confirm',
  'admin.orders.deliver': 'Delivered',
  'admin.orders.cancel': 'Cancel',
  'admin.orders.mark_paid': 'Bill paid',
  'admin.orders.transfer': 'Transfer table',
  'admin.orders.edit': 'Edit',
  'admin.orders.invalid_move': 'Invalid move: {from} → {to}.',
  'admin.orders.kitchen_only': 'This transition belongs to the kitchen — use the prep board.',
  'admin.orders.ready_toast': 'Order ready to serve',
  'admin.orders.moved_toast': 'Order moved to table {table}',
  'admin.orders.paid_toast': 'Bill marked as paid',
  'admin.orders.paid_err': 'Failed to mark as paid',
  'admin.orders.reorder_err': 'Failed to reorder',

  'admin.waiter.hint': 'Drag between columns to advance · within the column to prioritize',
  'admin.waiter.rt_on': 'realtime active',
  'admin.waiter.rt_off': 'reconnecting…',
  'admin.waiter.open': 'restaurant open',
  'admin.waiter.closed': 'restaurant closed',
  // Web Push (#124 PR-3) — control states (UX wording accepted by the PO)
  'admin.push.enable': 'Turn on notifications',
  'admin.push.scope_note':
    'Alerts you when a dish is ready or a table calls, even with the app closed. Everyone on the floor team with notifications on gets the same alerts.',
  'admin.push.enabled': 'Notifications on',
  'admin.push.blocked':
    'Notifications are blocked in this browser. Allow Pratzu notifications in the browser settings.',
  'admin.push.unsupported_install': 'Install the app to get notifications',
  'admin.push.install_ios_steps':
    'On iPhone or iPad, open Pratzu in Safari, tap Share and choose Add to Home Screen. Then open Pratzu from the new icon and turn on notifications.',
  'admin.push.unsupported':
    "This browser can't receive notifications. Alerts only show while Pratzu is open.",
  'admin.push.error': "Couldn't turn on notifications. Please try again.",
  // Notification content (API, features/push/texts.ts): table/state only,
  // NEVER customer data (criterion 6 of #124). {n} = table number.
  'admin.push.order_new_title': 'New order',
  'admin.push.order_new_body': 'New floor order',
  'admin.push.kitchen_new_title': 'New order',
  'admin.push.kitchen_new_body': 'Order to prepare',
  'admin.push.order_ready_title': 'Order ready',
  'admin.push.order_ready_body': 'Order ready to serve',
  'admin.push.call_waiter_title': 'Table calling',
  'admin.push.call_waiter_body': 'Floor call',
  'admin.push.table_number': 'Table {n}',
  'admin.push.channel_takeaway': 'Takeaway',
  'admin.push.channel_delivery': 'Delivery',
  // pedidos por mesa (0089) — faltavam no dicionário embutido em inglês
  'admin.waiter.flow_title': 'Orders by table',
  'admin.waiter.flow_empty': 'No orders sent',
  'admin.waiter.flow_sent': 'Sent',
  'admin.waiter.flow_ack': 'Received',
  'admin.waiter.flow_relayed': 'Entered locally',
  'admin.waiter.flow_ack_action': 'Confirm receipt',
  'admin.waiter.flow_relay_action': 'Enter in local system',
  'admin.waiter.bills_title': 'Bills to collect',
  'admin.waiter.bills_empty': 'No bills awaiting payment.',
  'admin.waiter.bill_confirm': 'Confirm receipt',
  'admin.waiter.bill_cancel': 'Cancel bill',
  'admin.waiter.bill_cancel_toast': 'Bill cancelled.',
  'admin.waiter.bill_paid_toast': 'Bill confirmed as paid.',
  'admin.waiter.bill_mode_single': 'One person pays it all',
  'admin.waiter.bill_mode_by_people': 'Split by people',
  'admin.waiter.bill_mode_flexible': 'Flexible split',
  'admin.waiter.bill_tip': 'Tip',
  'admin.waiter.pay_online': 'Paid online',
  'admin.waiter.paid_online_badge': 'paid online',
  'admin.waiter.online_paid': 'Already paid online',
  'admin.waiter.online_processing': 'Processing',
  'admin.waiter.online_due': 'To collect',
  'admin.waiter.payment_pending': 'pending',
  'admin.waiter.payment_processing': 'processing',
  'admin.waiter.payment_succeeded': 'paid',
  'admin.waiter.payment_failed': 'failed',
  'admin.waiter.payment_canceled': 'canceled',
  'admin.waiter.payment_refunded': 'refunded {amount}',
  'admin.waiter.bill_refund': 'Refund',
  'admin.waiter.bill_refund_confirm': 'Return {amount} to the customer',
  'admin.waiter.bill_refund_done': 'Refund issued ({id})',
  'admin.waiter.bill_refund_toast': 'Payment refunded.',
  'admin.waiter.bill_part_paid_toast': 'A share of the bill was paid online.',
  'admin.payments.title': 'Online payments',
  'admin.payments.subtitle':
    'Guests pay their share on their phone; the money goes straight to the restaurant via Stripe.',
  'admin.payments.state_ready': 'Ready to charge',
  'admin.payments.state_pending': 'Onboarding in progress',
  'admin.payments.state_off': 'Not set up',
  'admin.payments.charges': 'Charges enabled',
  'admin.payments.payouts': 'Payouts enabled',
  'admin.payments.details': 'Restaurant details complete',
  'admin.payments.start': 'Enable online payments',
  'admin.payments.resume': 'Continue setup',
  'admin.payments.hint':
    'Setup takes ~5 min on Stripe. Status updates automatically when you finish.',
  'admin.nav.payments': 'Payments',
  'admin.location.title': 'Location',
  'admin.location.subtitle':
    'The restaurant address on the map — guests use it to find you and the "Open in Google Maps" link.',
  'admin.location.current': 'Current location',
  'admin.location.none': 'No confirmed address yet.',
  'admin.location.state_confirmed': 'Confirmed',
  'admin.location.state_pending': 'Pending confirmation',
  'admin.location.confirmed_at': 'Confirmed on {date}.',
  'admin.location.suggestion_title': 'Suggestion found for your restaurant',
  'admin.location.suggestion_confidence': 'Search confidence: {pct}%',
  'admin.location.confirm': 'Confirm location',
  'admin.location.confirmed': 'Location confirmed.',
  'admin.location.change': 'Change address',
  'admin.location.open_maps': 'Open in Google Maps',
  'admin.location.search_label': 'Search address',
  'admin.location.search_placeholder': 'Street name and number',
  'admin.location.search_unavailable': 'Search unavailable right now. Retry or type it manually.',
  'admin.location.manual_toggle': 'Type it manually',
  'admin.location.search_toggle': 'Search on the map',
  'admin.location.street': 'Street and number',
  'admin.location.postal': 'Postal code',
  'admin.location.city': 'City',
  'admin.location.country': 'Country',
  'admin.location.pin_lat': 'Latitude (pin)',
  'admin.location.pin_lng': 'Longitude (pin)',
  'admin.location.pin_hint':
    'Adjust the coordinates if the entrance is elsewhere on the block (dark kitchen, back of the building…).',
  'admin.location.manual_hint':
    'No place on the map: guests get no route to you. Fits dark kitchens and non-public addresses.',
  'admin.location.manual_invalid': 'Fill in street and city.',
  'admin.location.confirm_check': 'I confirm this is the correct location of the restaurant.',
  'admin.location.save': 'Save location',
  'admin.location.saved': 'Location updated.',
  'admin.location.banner_title': 'Confirm your restaurant location.',
  'admin.location.banner_body_suggestion': 'We found a likely address — review and confirm it.',
  'admin.location.banner_body_missing': 'Your guests cannot find you on the map yet.',
  'admin.location.banner_cta': 'Confirm now',
  'admin.location.banner_link': 'View location',
  'admin.nav.location': 'Location',

  // ── restaurant country & currency (#486 item 1) ──
  'admin.nav.country_currency': 'Country & currency',
  'admin.country_currency.title': 'Country & currency',
  'admin.country_currency.subtitle':
    'The country and currency the restaurant uses for menu prices. Every country in the world is on the list.',
  'admin.country_currency.country': 'Restaurant country',
  'admin.country_currency.currency': 'Currency',
  'admin.country_currency.currency_hint':
    'The currency follows the selected country. Pick another one if the restaurant charges in a different currency.',
  'admin.country_currency.no_conversion':
    'Changing the country does not convert existing prices. New items start in the new currency.',
  'admin.country_currency.confirm_title': 'Confirm change',
  'admin.country_currency.confirm_country': 'Country: {from} → {to}',
  'admin.country_currency.confirm_currency': 'Currency: {from} → {to}',
  'admin.country_currency.confirm_no_conversion':
    'Saved prices stay as they are. New items start in the new currency.',
  'admin.country_currency.saved': 'Country and currency saved.',
  'admin.waiter.bill_people': 'people',
  'admin.waiter.bill_total': 'Bill total',
  'admin.waiter.to_pay': '{count} bill(s) to collect',
  'admin.waiter.calling': '{count} table(s) calling',
  'admin.waiter.new_order': '+ New order',
  'admin.waiter.empty': 'No orders in progress',
  'admin.waiter.empty_hint': 'Orders appear here automatically.',
  'admin.waiter.col.pending': 'New',
  'admin.waiter.col.confirmed': 'Confirmed',
  'admin.waiter.col.preparing': 'Preparing',
  'admin.waiter.col.ready': 'Ready',
  'admin.waiter.col.to_pay': 'To pay',

  'admin.calls.calling': 'Table {table} is calling',
  'admin.calls.waiting': 'calling for {time}',
  'admin.calls.attend': 'Attended',
  'admin.calls.attend_err': 'Failed to attend',

  'admin.kitchen.hint': 'Drag between columns to advance · within the column to prioritize',
  'admin.kitchen.new_order_toast': 'New order received',
  'admin.kitchen.status_err': 'Failed to update status',
  'admin.kitchen.item_err': 'Failed to mark item',
  'admin.kitchen.all_done_toast': 'All items done — waiters notified',
  'admin.kitchen.filter_active': 'Active',
  'admin.kitchen.filter_all': 'All',
  'admin.kitchen.empty': 'No orders in queue',
  'admin.kitchen.empty_hint': 'New orders appear here automatically.',
  'admin.kitchen.col.pending': 'New',
  'admin.kitchen.col.confirmed': 'Confirmed',
  'admin.kitchen.col.preparing': 'Preparing',
  'admin.kitchen.col.ready': 'Ready',
  'admin.kitchen.col.delivered': 'Delivered',
  'admin.kitchen.col.cancelled': 'Cancelled',
  'admin.kitchen.waiter_only': 'The kitchen cannot do this transition — use the waiter board.',
  'admin.kitchen.finish_item': 'Done',
  'admin.kitchen.start_preparing': 'Start preparing',
  'admin.kitchen.mark_ready': 'Mark ready',
  'admin.kitchen.ready_waiting': 'Ready — waiting for the waiter to deliver',

  'admin.login.subtitle': 'Restaurant panel',
  'admin.login.title': 'Sign in',
  'admin.login.email_hint': 'Enter your email to receive your access code.',
  'admin.login.google_btn': 'Continue with Google',
  'admin.login.or': 'or',
  'admin.login.email': 'Email',
  'admin.login.email_ph': 'you@restaurant.com',
  'admin.login.send_code': 'Send code',
  'admin.login.verify_title': 'Verify code',
  'admin.login.code_sent_prefix': 'Code sent to',
  'admin.login.code_sent_to': 'Code sent to {email}',
  'admin.login.code_label': '6-digit code',
  'admin.login.use_another_email': 'Use another email',
  'admin.login.choose_title': 'Choose access',
  'admin.login.choose_hint': 'Where do you want to sign in,',
  'admin.login.enter_as': 'Sign in as {role}',
  'admin.login.role_owner': 'Owner',
  'admin.login.role_platform_owner': 'Platform Owner',
  'admin.login.google_err': 'Google sign-in not completed — try again or use the email code.',
  'admin.login.google_failed': 'Google sign-in failed',
  'admin.login.no_access':
    'No access found for this email. Ask the restaurant owner to invite you under Users (Kitchen, Waiter or Owner).',
  'admin.login.send_code_err': 'Failed to send code',
  'admin.login.invalid_code': 'Invalid or expired code',
  'admin.login.signin_err': 'Failed to sign in',
  'admin.dashboard.welcome_title': 'Welcome to the platform',
  'admin.dashboard.welcome_hint': 'Pick a restaurant in the sidebar to get started.',
  'admin.dashboard.default_name': 'My Restaurant',
  'admin.dashboard.subtitle': 'Restaurant Owner panel',
  'admin.dashboard.open': '● Open',
  'admin.dashboard.closed': '● Closed',
  'admin.dashboard.stats_categories': 'Active categories',
  'admin.dashboard.stats_products': 'Active products',
  'admin.dashboard.stats_tables': 'Active tables',
  'admin.dashboard.quick_actions': 'Quick actions',
  'admin.dashboard.link_branding': 'Set up branding',
  'admin.dashboard.link_branding_desc': 'Colors, logo and hours',
  'admin.dashboard.link_menu': 'Manage menu',
  'admin.dashboard.link_menu_desc': 'Categories and products',
  'admin.dashboard.link_combos': 'Combos and promotions',
  'admin.dashboard.link_combos_desc': 'Grouped discounts',
  'admin.dashboard.link_tables': 'Generate tables and QR codes',
  'admin.dashboard.link_tables_desc': 'Tables with printable QR',
  'admin.dashboard.view_menu': 'View menu',
  'admin.dashboard.takeaway_delivery': 'Take-away / Delivery',
  'admin.dashboard.takeaway_delivery_desc': 'Opens the public ordering menu',
  'admin.dashboard.table_menu': 'Table menu',
  'admin.dashboard.table_menu_desc': 'Pick a table and open the menu',
  'admin.dashboard.status': 'Status',
  'admin.dashboard.primary_locale': 'Primary language',
  'admin.dashboard.takeaway': 'Take-away',
  'admin.dashboard.delivery': 'Delivery',
  'admin.dashboard.tip': 'Tip',
  'admin.dashboard.tables_total': 'Total tables',
  'admin.dashboard.choose_table': 'Choose table',
  'admin.dashboard.no_active_tables': 'No active tables.',
  'admin.dashboard.create_tables': 'Create tables →',
  'admin.dashboard.table_number': 'Table {number}',

  'admin.nav.kitchens': 'Kitchens',
  'admin.kitchens.title': 'Kitchens (stations)',
  'admin.kitchens.subtitle': 'Split preparation into stations — each kitchen sees only its items',
  'admin.kitchens.main': 'Main kitchen',
  'admin.kitchens.main_hint': 'Unassigned products are prepared here (default)',
  'admin.kitchens.products': '{n} products',
  'admin.kitchens.name_placeholder': 'e.g. Bar, Grill, Desserts',
  'admin.kitchens.create': '+ Create kitchen',
  'admin.kitchens.created': 'Kitchen created',
  'admin.kitchens.deleted': 'Kitchen deleted — products moved to main',
  'admin.kitchens.confirm_delete': 'Delete "{name}"? Products move to the main kitchen.',
  'admin.kitchens.empty': 'Only the main kitchen',
  'admin.kitchens.empty_hint': 'Create stations (e.g. bar, grill) to route products',
  'admin.kitchens.active': 'active',
  'admin.kitchens.inactive': 'inactive',
  'admin.kitchens.delete': 'delete',
  'admin.kitchens.assign_title': 'Route category',
  'admin.kitchens.assign_hint': 'Moves every product in the category to the chosen kitchen',
  'admin.kitchens.assign_apply': 'Apply',
  'admin.kitchens.assigned': '{n} products routed',
  'admin.kitchens.no_categories': 'Create a category first',
  'admin.kitchen.all_stations': 'All',
  'admin.kitchen.waiting_stations': 'Waiting for other kitchens',
  'admin.kitchen.ready_blocked': 'The order is ready only when every kitchen finishes its items',
  'admin.menu.kitchen': 'Kitchen',
  'admin.menu.kitchen_hint': 'Where this product is prepared',
  'admin.tables.title': 'Tables & QR Codes',
  'admin.tables.subtitle': 'Manage tables and print QR codes',
  'admin.tables.print_all': '🖨 Print all',
  'admin.tables.generate_cta': '+ Generate tables',
  'admin.tables.generate_tables': 'Generate tables',
  'admin.tables.generate': 'Generate',
  'admin.tables.empty': 'No tables yet',
  'admin.tables.empty_hint': 'Generate tables to create the QR codes',
  'admin.tables.stat_active': 'Active',
  'admin.tables.stat_inactive': 'Inactive',
  'admin.tables.activate': 'Activate',
  'admin.tables.deactivate': 'Deactivate',
  'admin.tables.mesa': 'Table {n}',
  'admin.tables.qr_alt': 'QR Table {n}',
  'admin.tables.scan_to_order': 'Scan to order',
  'admin.tables.scan_hint': 'Point your phone camera at the QR code',
  'admin.tables.created_toast': '{count} tables created!',
  'admin.tables.qty': 'Number of tables',
  'admin.tables.qty_hint': 'Up to 200 at a time',
  'admin.tables.start_from': 'Number starting from',
  'admin.tables.start_from_hint': 'Existing numbers are skipped automatically',
  'admin.tables.preview':
    '{count} tables will be created, numbered starting from {start}. Each table gets a unique QR code.',
  'admin.tables.qr_print_err': 'Failed to generate QR codes',
  'admin.tables.regen_ok': 'QR regenerated!',
  'admin.tables.qr_title': 'QR Code — Table {n}',
  'admin.tables.qr_url': 'QR URL',
  'admin.tables.regen': 'Regenerate QR',
  'admin.tables.download_png': 'Download PNG',
  'admin.tables.regen_hint':
    'Regenerating invalidates the previous QR code. Only use it if the current one has been compromised.',
  'admin.transfer.moved_toast': 'Order moved to {table}',
  'admin.transfer.err': 'Failed to transfer',
  'admin.transfer.transferring': 'Transferring…',
  'admin.transfer.submit': 'Transfer',
  'admin.transfer.hint_start': 'Order from',
  'admin.transfer.hint_end':
    'will be moved to the selected table. The kitchen and dining screens update automatically.',
  'admin.transfer.no_options': 'There are no other active tables to transfer to.',
  'admin.transfer.target': 'Destination table',
  'admin.kanban.empty': 'No orders',
  'admin.kanban.order_count': '{count} order(s)',

  'admin.branding.subtitle': 'Visual identity and restaurant settings',
  'admin.branding.saved_toast': 'Saved!',
  'admin.branding.menu_template': 'Menu template',
  'admin.branding.menu_template_desc': 'How the menu appears to the customer.',
  'admin.branding.menu_template_list': 'List (classic)',
  'admin.branding.menu_template_list_desc': 'Compact list with a small photo on the right.',
  'admin.branding.menu_template_grid': 'Grid with large photos',
  'admin.branding.menu_template_grid_desc':
    'Two photos per row, product carousel and allergy filters.',
  'admin.branding.menu_template_feed1': 'Feed (1 photo per row)',
  'admin.branding.menu_template_feed1_desc':
    'Each dish becomes a post with a full-screen photo and carousel.',
  'admin.branding.ops_order_flow': 'Table order flow',
  'admin.branding.ops_order_flow_full': 'Full (kitchen and floor)',
  'admin.branding.ops_order_flow_full_desc':
    'The order goes to the kitchen and the floor, as today.',
  'admin.branding.ops_order_flow_waiter': 'Via waiter (local system)',
  'admin.branding.ops_order_flow_waiter_desc':
    'The customer sends the order to the waiter, who enters it in the restaurant’s local system.',
  'admin.branding.general_title': 'General information',
  'admin.branding.about': 'About the restaurant',
  'admin.branding.about_ph': 'Short description shown on the customer menu...',
  'admin.branding.logo': 'Logo',
  'admin.branding.logo_updated_toast': 'Logo updated!',
  'admin.branding.logo_change': 'Change logo',
  'admin.branding.logo_upload': 'Upload logo',
  'admin.branding.logo_hint': 'JPEG, PNG or WebP · max 5MB',
  'admin.branding.locale_updated_toast': 'Language updated!',
  'admin.branding.locale_title': 'Language and currency',
  'admin.branding.locale_desc': 'Sets the menu language and the currency shown in prices',
  'admin.branding.primary_locale': 'Primary language',
  'admin.branding.save_locale': 'Save language',
  'admin.branding.locales_saved_toast':
    'Languages saved — generating the missing translations with AI',
  'admin.branding.retranslation_toast':
    'Retranslation started — {n} AI draft(s) regenerated from scratch',
  'admin.branding.target_locales_title': 'Translation languages',
  'admin.branding.target_locales_desc':
    'Languages the menu is automatically translated into by AI (products, ingredients, variations and add-ons)',
  'admin.branding.target_locales_hint':
    'The primary language ({primary}) is the base — it is not translated. Saving generates the missing translations (large menus or many languages may take a few minutes); new items are translated automatically when created or edited.',
  'admin.branding.save_target_locales': 'Save translation languages',
  'admin.branding.refresh_translations': 'Refresh translations',
  'admin.branding.translations_status': 'Translation status',
  'admin.branding.colors_published_toast': 'Colors published to the menu!',
  'admin.branding.color_background': 'Store background',
  'admin.branding.color_background_help':
    'Background color used across the customer menu screen — product list, product detail and cart.',
  'admin.branding.color_header': 'Header',
  'admin.branding.color_header_help':
    'Background of the top strip with the logo and restaurant name, on the menu home screen.',
  'admin.branding.color_button': 'Buttons',
  'admin.branding.color_button_help':
    'Color of the main menu buttons, such as "Add to order" and the featured categories.',
  'admin.branding.color_button_active': 'Pressed button',
  'admin.branding.color_button_active_help':
    'Color shown for a moment when the customer taps a button — visual touch feedback.',
  'admin.branding.color_selected': 'Selection',
  'admin.branding.color_selected_help':
    'Color used to indicate categories, variations or options that are currently selected.',
  'admin.branding.color_logo_bg': 'Logo highlight background',
  'admin.branding.color_logo_bg_help':
    'Background of the highlight area at the top of the menu. Filled in automatically when the logo is uploaded — empty = detect from the image.',
  'admin.branding.colors_title': 'Color palette',
  'admin.branding.colors_desc':
    'Preview before publishing — colors only take effect on the customer menu after you click Save',
  'admin.branding.color_auto_ph': 'auto (from logo)',
  'admin.branding.color_use_auto': 'use auto',
  'admin.branding.colors_publish': 'Save and publish to the menu',
  'admin.branding.colors_reset': 'Restore default',
  'admin.branding.colors_preview_hint':
    'Preview — tap and hold the button to see the "pressed" color',
  'admin.branding.ops_open': 'Restaurant open',
  'admin.branding.ops_open_desc': 'Manual control, independent of opening hours',
  // Opening mode (#332, 0080): replaces the toggle with 3 options
  'admin.branding.ops_open_mode': 'Opening mode',
  'admin.branding.ops_open_mode_auto': 'Automatic (follows the schedule)',
  'admin.branding.ops_open_mode_open': 'Open now',
  'admin.branding.ops_open_mode_closed': 'Closed',
  'admin.branding.ops_open_mode_auto_desc': 'Opens and closes according to the opening hours.',
  'admin.branding.ops_open_mode_open_desc': 'Accepts orders now, even outside opening hours.',
  'admin.branding.ops_open_mode_closed_desc': 'Does not accept orders, even during opening hours.',
  'admin.branding.ops_takeaway': 'Take-away',
  'admin.branding.ops_takeaway_desc': 'Accept pickup orders',
  'admin.branding.ops_delivery': 'Delivery',
  'admin.branding.ops_delivery_desc': 'Accept delivery orders',
  'admin.branding.ops_tip_mode': 'Tip mode',
  'admin.branding.ops_tip_none': 'No tip',
  'admin.branding.ops_tip_optional': 'Optional',
  'admin.branding.ops_tip_suggested': 'Suggested',
  'admin.branding.ops_fiscal_mode': 'Tax ID at checkout',
  'admin.branding.ops_fiscal_mode_desc':
    'Ask for the customer NIF/CPF when ordering — it goes on the fiscal receipt.',
  'admin.branding.ops_fiscal_off': 'Do not ask',
  'admin.branding.ops_fiscal_optional': 'Optional (free field)',
  'admin.branding.ops_fiscal_required': 'Required for customers',
  'admin.branding.ops_sla': 'Kitchen wait SLA (min)',
  'admin.branding.ops_sla_desc':
    'Orders waiting longer than this get a red highlight on the kitchen monitor.',
  'admin.branding.ops_sla_invalid': 'Enter the SLA in minutes (1 to 240).',
  'admin.branding.ops_save': 'Save operation settings',
  // #132: advanced waiter
  'admin.waiter.my_tables': 'My tables',
  'admin.waiter.my_tables_hint': 'Tap the tables you serve to filter the floor.',
  'admin.waiter.my_tables_none': 'Tap a table to take over service',
  'admin.waiter.my_tables_all': 'Show all tables',
  'admin.waiter.my_tables_mine': 'Only mine',
  'admin.tables.merge': 'Merge into…',
  'admin.tables.merge_title': 'Merge table {from} into table {to}',
  'admin.tables.merge_hint':
    'Open orders and the active bill of table {from} move to table {to}. Table {from} becomes inactive. Audited action.',
  'admin.tables.merge_done': 'Tables merged: {from} → {to}',
  'admin.tables.merge_pick': 'Target table',
  'admin.tables.merge_no_target': 'There is no other active table to merge into.',
  'admin.import.review_item_ok': 'Correct',
  'admin.import.review_item_fix': 'Needs change',
  'admin.import.review_item_removed': 'Removed',
  'admin.import.review_pending': 'Pending',
  'admin.import.review_comment_ph': 'What needs to be removed, added or changed in this item?',
  'admin.import.review_group_ph': 'Comment for the group (applies to all items)',
  'admin.import.review_mark_all': 'Mark all as correct',
  'admin.import.review_progress': 'Reviewed {n} of {total}',
  'admin.import.review_submit': 'Submit review',
  'admin.import.review_submit_hint':
    'Confirm each item with ✓, comment what needs changing or remove it — then submit everything at once.',
  'admin.import.review_fixed': 'adjusted by AI',
  'admin.import.review_undo_remove': 'Undo removal',
  'admin.import.reviewing_title': 'AI is applying your comments… {done} of {total}',
  'admin.import.reviewing_hint':
    'When done, the new version shows up here for you to check before importing.',
  'admin.import.review_group_fix': 'Mark all as needing change',
  'admin.import.review_group_hint':
    'The group comment applies to all its items — items with their own comment take priority.',
  'admin.import.translation_found': 'Menu has a printed {locale} translation for this item',
  'admin.import.kcal_label': 'kcal',
  'admin.import.service_charge_found':
    'Service charge read from the menu: {charge} — stored as information only, never charged automatically.',
  'admin.import.review_modal_title': 'Unreviewed items',
  'admin.import.review_modal_body':
    '{n} items are still unmarked (the others were already sent with your comments). Were these remaining ones read correctly?',
  'admin.import.review_modal_all_ok': 'Yes, they are correct',
  'admin.import.review_modal_keep': 'I want to keep reviewing',
  'admin.import.review_applied_toast':
    'Review applied: {applied} adjustment(s), {removed} removed.',
  'admin.import.review_pending_warn':
    'Some items are unreviewed — the assistant will ask for confirmation.',
  'admin.import.nav': 'Import Menu',
  'admin.import.title': 'Import Menu',
  'admin.import.subtitle': 'Send page photos or a PDF — reading is automatic.',
  'admin.import.drop_hint': 'Drop photos or a PDF here, or tap to choose',
  'admin.import.pages': 'page(s)',
  'admin.import.send': 'Upload and read menu',
  'admin.import.processing': 'Reading the menu…',
  'admin.import.page_progress': 'Page {n} of {total}',
  'admin.import.review_title': 'Review import',
  'admin.import.language': 'Menu language',
  'admin.import.confirm_language': 'Confirm the detected language',
  // #299 (PR-2) + #474 (UX review): unrecognised price (0.00/market) —
  // product/variant are paused until the owner sets the price in review
  'admin.import.price_confirm_badge': 'Confirm price',
  'admin.import.price_original': 'Price printed on the menu: {raw}',
  'admin.import.price_confirm_hint':
    'Without a price, the dish stays paused and does not appear on the Menu.',
  'admin.import.price_confirm_hint_option':
    'Without a price, this option does not appear on the Menu.',
  'admin.import.price_still_missing':
    'The price of this dish was not changed. Type it in the price field.',
  'admin.import.price_of': 'Price for {name}',
  'admin.import.price_base_from_variants': 'Base price = the lowest of the options with a price',
  'admin.import.price_filter': 'Needs price',
  'admin.import.price_invalid': 'Invalid price.',
  'admin.import.nothing_to_confirm': 'Nothing to confirm.',
  'admin.import.see_all': 'See all',
  'admin.import.paused_dishes_no_price': 'Dishes paused for lack of a price: {n}',
  'admin.import.paused_options_no_price': 'Options paused for lack of a price: {n}',
  'admin.import.detected': 'detected',
  'admin.import.restaurant_info': 'Restaurant info',
  'admin.import.apply_info': 'Apply name, about and logo to the restaurant',
  'admin.import.categories': 'Categories',
  'admin.import.products_count': 'product(s)',
  'admin.import.extras': 'Extras',
  'admin.import.variants': 'Variants',
  'admin.import.ingredients': 'Ingredients',
  'admin.import.has_photo': 'has photo in menu',
  'admin.import.remove': 'Remove',
  'admin.import.mode': 'On import',
  'admin.import.append': 'Add to current menu',
  'admin.import.replace': 'Replace current menu',
  'admin.import.replace_warn': 'Current Menu items will be deactivated.',
  'admin.import.commit': 'Import Menu',
  'admin.import.committing': 'Importing…',
  'admin.import.done_title': 'Menu imported!',
  'admin.import.done_desc': 'categories and products created in the menu language.',
  'admin.import.error': 'Could not read the menu. Try again.',
  'admin.import.cancel': 'Cancel import',
  'admin.import.no_items': 'No items recognized — check the photos.',
  'admin.import.name': 'Name',
  'admin.import.description': 'Description',
  'admin.import.price': 'Price',
  'admin.import.open_menu': 'Open menu',
  'admin.import.chat_placeholder': 'Reply to the assistant…',
  'admin.import.send_photo': 'Send photo',
  'admin.import.review_cta': 'Review and import',
  'admin.import.replaced_page': 'Photo for page {n} replaced — re-reading…',
  'admin.import.start': 'Reading the menu…',
  'admin.branding.pay_title': 'Payments',
  'admin.branding.pay_hint': 'Payment methods offered when closing the bill at the table.',
  'admin.branding.pay_cash': 'Cash',
  'admin.branding.pay_card': 'Card on site',
  'admin.branding.pay_saved': 'Payment methods updated.',
  'admin.branding.tz_title': 'Time zone',
  'admin.branding.tz_desc':
    'Restaurant time zone: opening hours, promotions and the dish of the day follow this clock.',
  'admin.branding.tz_label': 'Time zone (IANA)',
  'admin.branding.tz_now': 'Now at the restaurant:',
  'admin.branding.tz_save': 'Save time zone',
  'admin.branding.tz_saved': 'Time zone updated.',
  'admin.branding.hours_saved_toast': 'Hours saved!',
  'admin.branding.hours_title': 'Opening hours',
  'admin.branding.hours.day_0': 'Sunday',
  'admin.branding.hours.day_1': 'Monday',
  'admin.branding.hours.day_2': 'Tuesday',
  'admin.branding.hours.day_3': 'Wednesday',
  'admin.branding.hours.day_4': 'Thursday',
  'admin.branding.hours.day_5': 'Friday',
  'admin.branding.hours.day_6': 'Saturday',
  'admin.branding.hours.until': 'to',
  'admin.branding.hours.closed': 'Closed',
  'admin.branding.hours_save': 'Save hours',
  'admin.branding.hours_incomplete':
    'Opening or closing time missing for: {days}. Fill in both times or leave the day empty.',

  'admin.combos.subtitle': 'Group products with a discount',
  'admin.combos.new': '+ New combo',
  'admin.combos.empty': 'No combos yet',
  'admin.combos.empty_hint': 'Group 2 or more products and offer a discount',
  'admin.combos.create': 'Create combo',
  'admin.combos.create_short': 'Create',
  'admin.combos.deactivate': 'Deactivate',
  'admin.combos.deactivated_toast': 'Combo deactivated',
  'admin.combos.updated_toast': 'Combo updated!',
  'admin.combos.created_toast': 'Combo created!',
  'admin.combos.edit_title': 'Edit combo',
  'admin.combos.new_title': 'New combo',
  'admin.combos.name': 'Combo name',
  'admin.combos.discount_type': 'Discount type',
  'admin.combos.discount_percent': 'Percentage (%)',
  'admin.combos.discount_fixed': 'Fixed amount',
  'admin.combos.discount_value_percent': 'Discount (%)',
  'admin.combos.discount_value_fixed': 'Discount',
  'admin.combos.items': 'Combo products (min. 2)',
  'admin.combos.add_product': 'Add product…',
  'admin.combos.items_min_hint': 'Select at least 2 products',
  'admin.taxes.title': 'Tax rules',
  'admin.taxes.subtitle': 'Set up taxes per product category',
  'admin.taxes.new': '+ New rule',
  'admin.taxes.empty': 'No rules yet',
  'admin.taxes.empty_hint': 'Set up the taxes that apply to each product category',
  'admin.taxes.create': 'Create rule',
  'admin.taxes.create_short': 'Create',
  'admin.taxes.active': 'Active',
  'admin.taxes.inactive': 'Inactive',
  'admin.taxes.inclusive': 'included in the price',
  'admin.taxes.exclusive': 'on top of the price',
  'admin.taxes.updated_toast': 'Rule updated!',
  'admin.taxes.created_toast': 'Rule created!',
  'admin.taxes.edit_title': 'Edit tax rule',
  'admin.taxes.new_title': 'New tax rule',
  'admin.taxes.category': 'Product category',
  'admin.taxes.cat_food': 'Food (food)',
  'admin.taxes.cat_beverage': 'Beverage (beverage)',
  'admin.taxes.cat_tobacco': 'Tobacco (tobacco)',
  'admin.taxes.cat_other': 'Other',
  'admin.taxes.rate': 'Rate (%)',
  'admin.taxes.rate_hint': 'e.g. 13 for 13%, 6.5 for 6.5%',
  'admin.taxes.label': 'Label',
  'admin.taxes.label_ph': 'VAT 13%',
  'admin.taxes.inclusive_label': 'Included in price',
  'admin.taxes.inclusive_hint': 'Tax is already included in the price shown to the customer',
  'admin.users.title': 'Restaurant users',
  'admin.users.invite_new': '+ Invite',
  'admin.users.empty': 'No users yet',
  'admin.users.empty_hint': 'Invite the first user to this restaurant.',
  'admin.users.invite': 'Invite',
  'admin.users.inviting': 'Inviting…',
  'admin.users.invited_toast': 'User invited successfully',
  'admin.users.invite_err': 'Failed to invite user',
  'admin.users.invite_title': 'Invite user',
  'admin.users.invite_title_named': 'Invite user — {name}',
  'admin.users.roles_hint': 'Roles (you can pick more than one)',
  'admin.users.roles_required': 'Select at least one role',
  'admin.users.role_owner': 'Owner',
  // ── Gerente + ajustes de preço com aprovação (#284 / #274) ──
  'admin.nav.adjustments': 'Price adjustments',
  'admin.users.role_manager': 'Manager',
  'admin.plan.managers': 'Managers',
  'admin.plans.col_managers': 'Managers',
  'admin.plans.managers_hint': '0 = no Manager role; empty = unlimited',
  'admin.adjust.button_request': 'Request adjustment',
  'admin.adjust.button_apply': 'Adjust price',
  'admin.adjust.title_request': 'Request a price adjustment',
  'admin.adjust.title_apply': 'Adjust price',
  'admin.adjust.needs_approval':
    'The adjustment is only applied after a Manager or the Owner approves it.',
  'admin.adjust.target': 'Apply to',
  'admin.adjust.target_order': 'Order total',
  'admin.adjust.target_item': 'Dish',
  'admin.adjust.type': 'Adjustment type',
  'admin.adjust.type_discount_percent': 'Discount (%)',
  'admin.adjust.type_discount_amount': 'Discount (amount)',
  'admin.adjust.type_price_override': 'Manual price',
  'admin.adjust.type_none': 'Remove adjustment',
  'admin.adjust.value_percent': 'Percentage (%)',
  'admin.adjust.value_money': 'Amount',
  'admin.adjust.value_required': 'Enter a value greater than zero',
  'admin.adjust.reason': 'Reason',
  'admin.adjust.reason_optional': 'Reason (optional)',
  'admin.adjust.reason_required': 'Enter the reason',
  'admin.adjust.request': 'Send request',
  'admin.adjust.apply': 'Apply',
  'admin.adjust.requested_toast': 'Adjustment request sent for approval',
  'admin.users.seats': 'Plan seats per role',
  'admin.users.edit_roles': 'Roles',
  'admin.users.edit_roles_title': 'Roles for {email}',
  'admin.users.roles_seat_hint':
    'A person can hold several roles at once; each role uses one seat of the plan limit.',
  'admin.users.roles_saved_toast': 'Roles updated',
  'admin.users.roles_err': 'Could not save the roles',
  'admin.waiter_limit.title': 'Waiter limit for price adjustments',
  'admin.waiter_limit.desc':
    'Waiter discounts and surcharges up to this limit apply immediately (audited). Above the limit — or with no limit — they become a request for the Manager or Owner to approve.',
  'admin.waiter_limit.mode': 'Limit type',
  'admin.waiter_limit.mode_none': 'No limit (everything needs approval)',
  'admin.waiter_limit.mode_percent': 'Percentage of the price',
  'admin.waiter_limit.mode_amount': 'Fixed amount',
  'admin.waiter_limit.value_percent': 'Limit (%)',
  'admin.waiter_limit.value_amount': 'Limit (amount per adjustment)',
  'admin.waiter_limit.save': 'Save limit',
  'admin.waiter_limit.invalid':
    'Invalid limit: use a % from 0 to 100 or an amount of zero or more.',
  'admin.adjust.auto_applied_toast': 'Adjustment applied (within your limit)',
  'admin.adjust.auto_approved_badge': 'Auto-approved (within limit)',
  'admin.adjust.applied_toast': 'Adjustment applied',
  'admin.adjust.new_request_toast': 'New adjustment request — table {table}',
  'admin.adjust.your_approved_toast': 'Your adjustment request was approved',
  'admin.adjust.your_rejected_toast': 'Your adjustment request was rejected: {note}',
  'admin.adjust.page_title': 'Price adjustments',
  'admin.adjust.page_hint': "Approve or reject the team's adjustment requests",
  'admin.adjust.tab_pending': 'Pending',
  'admin.adjust.tab_audit': 'Audit',
  'admin.adjust.empty': 'No pending requests',
  'admin.adjust.empty_hint': "Waiters' adjustment requests show up here in real time.",
  'admin.adjust.approve': 'Approve',
  'admin.adjust.reject': 'Reject',
  'admin.adjust.reject_title': 'Reject adjustment request',
  'admin.adjust.reject_reason': 'Reason for rejection',
  'admin.adjust.approved_toast': 'Adjustment approved and applied',
  'admin.adjust.rejected_toast': 'Adjustment request rejected',
  'admin.adjust.requested_by': 'Requested by {name}',
  'admin.adjust.decided_by': 'Decided by {name}',
  'admin.adjust.rule_none': 'no adjustment',
  'admin.adjust.audit_applied': 'Applied adjustments',
  'admin.adjust.audit_requests': 'Rejected, cancelled or pending requests',
  'admin.adjust.col_when': 'When',
  'admin.adjust.col_change': 'Adjustment',
  'admin.adjust.col_total': 'Order total',
  'admin.adjust.col_requested': 'Requested by',
  'admin.adjust.col_decided': 'Applied by',
  'admin.adjust.col_reason': 'Reason',
  'admin.adjust.status_pending': 'Pending',
  'admin.adjust.status_approved': 'Approved',
  'admin.adjust.status_rejected': 'Rejected',
  'admin.adjust.status_cancelled': 'Cancelled',
  'admin.users.role_kitchen': 'Kitchen',
  'admin.users.role_waiter': 'Waiter',
  'admin.users.role_customer': 'Customer',
  'admin.users.email': 'Email',
  'admin.users.email_ph': 'user@example.com',
  'admin.users.name': 'Name',
  'admin.users.name_optional': 'Name (optional)',
  'admin.users.name_ph': 'e.g. Maria Santos',
  'admin.users.role': 'Role',
  'admin.users.status': 'Status',
  'admin.users.active': 'Active',
  'admin.users.inactive': 'Inactive',
  'admin.users.deactivate': 'Deactivate',
  'admin.users.deactivate_confirm': 'Deactivate {email}?',
  'admin.users.deactivated_toast': 'User deactivated',
  'admin.users.deactivate_err': 'Failed to deactivate user',
  'admin.users.reactivate': 'Reactivate',
  'admin.users.reactivate_confirm': 'Reactivate {email}?',
  'admin.users.reactivated_toast': 'User reactivated',
  'admin.users.reactivate_err': 'Failed to reactivate user',
  'admin.users.load_err': 'Failed to load users',
  'admin.impersonation.banner': 'Platform session in {restaurant} · ends at {time}',
  'admin.impersonation.banner_soon': '5 minutes left · ends at {time}',
  'admin.impersonation.banner_reason': 'Reason: {reason}',
  'admin.impersonation.end': 'End',
  'admin.impersonation.end_aria': 'End the platform session in {restaurant}',
  'admin.impersonation.end_err': 'Could not end the session. Try again.',
  'admin.impersonation.expired': 'The platform session has ended.',
  'admin.impersonation.expired_notice': 'The platform session in {restaurant} ended at {time}.',
  'admin.impersonation.enter_again': 'Open again',
  'admin.impersonation.switch_confirm': 'End the session in {restaurant}?',
  'admin.impersonation.start_title': 'Open {restaurant} as Pratzu staff',
  'admin.impersonation.reason_label': 'Reason',
  'admin.impersonation.reason_hint': 'The restaurant sees this reason in the access list.',
  'admin.impersonation.reason_required': 'Enter a reason.',
  'admin.impersonation.reason_length': 'The reason must be 3 to 500 characters.',
  'admin.impersonation.start': 'Enter',
  'admin.impersonation.start_err': 'Could not start the session. Try again.',
  'admin.impersonation.err_already':
    'You already have a platform session open. End it before entering another restaurant.',
  'admin.impersonation.err_inactive': 'This restaurant is inactive.',
  'admin.impersonation.access_title': 'Pratzu team access',
  'admin.impersonation.access_who': 'Pratzu team',
  'admin.impersonation.access_empty': 'No Pratzu team access yet.',
  'admin.impersonation.access_err': 'Could not load access history.',
  'admin.impersonation.access_retry': 'Try again',
  'admin.impersonation.access_more': 'Load more',
  'admin.impersonation.access_expired': '{duration} · timed out',
  'admin.impersonation.access_active': 'In progress',
  'admin.impersonation.access_col_when': 'Date and time',
  'admin.impersonation.access_col_duration': 'Duration',
  'admin.impersonation.access_col_who': 'Who',
  'admin.impersonation.access_col_reason': 'Reason',
  'admin.platform.users_title': 'Platform users',
  'admin.platform.users_subtitle': 'Platform owners with full access to the platform',
  'admin.platform.users_invite_new': '+ Invite',
  'admin.platform.users_empty': 'No users yet',
  'admin.platform.users_empty_hint': 'Invite the first platform administrator.',
  'admin.platform.users_invite': 'Invite',
  'admin.platform.users_inviting': 'Inviting…',
  'admin.platform.users_invited_toast': 'User invited successfully',
  'admin.platform.users_invite_err': 'Failed to invite user',
  'admin.platform.users_invite_title': 'Invite platform owner',
  'admin.platform.users_invite_hint':
    'The user will get full access to the platform. They can sign in with OTP via the email provided.',
  'admin.platform.users_email': 'Email',
  'admin.platform.users_email_ph': 'admin@example.com',
  'admin.platform.users_name': 'Name',
  'admin.platform.users_name_optional': 'Name (optional)',
  'admin.platform.users_name_ph': 'e.g. João Silva',
  'admin.platform.users_status': 'Status',
  'admin.platform.users_active': 'Active',
  'admin.platform.users_inactive': 'Inactive',
  'admin.platform.users_deactivate': 'Deactivate',
  'admin.platform.users_deactivate_confirm': 'Deactivate {email}?',
  'admin.platform.users_deactivated_toast': 'User deactivated',
  'admin.platform.users_deactivate_err': 'Failed to deactivate user',
  'admin.platform.users_reactivate': 'Reactivate',
  'admin.platform.users_reactivate_confirm': 'Reactivate {email}?',
  'admin.platform.users_reactivated_toast': 'User reactivated',
  'admin.platform.users_reactivate_err': 'Failed to reactivate user',
  'admin.features.ai_translation': 'Automatic translation',
  'admin.features.menu_import_ocr': 'Menu import (OCR)',
  'admin.features.promotions': 'Promotions',
  'admin.features.updated_toast': 'Feature updated.',
  'admin.features.enabled_hint': 'Enabled — click to disable',
  'admin.features.disabled_hint': 'Disabled — click to enable',
  'admin.nav.platform_plans': 'Bundles',
  'admin.plans.title': 'Bundles (subscription plans)',
  'admin.plans.subtitle': 'Prices, limits and Stripe integration for each plan.',
  'admin.plans.load_err': 'Failed to load plans.',
  'admin.plans.save_err': 'Failed to save plan.',
  'admin.plans.saved_toast': 'Plan updated.',
  'admin.plans.empty': 'No plans configured.',
  'admin.plans.empty_hint': 'Check the billing migration (0040).',
  'admin.plans.col_plan': 'Plan',
  'admin.plans.col_price': 'Price',
  'admin.plans.col_kitchen': 'Kitchen staff',
  'admin.plans.col_waiters': 'Waiters',
  'admin.plans.col_translations': 'Translation languages',
  'admin.plans.col_stripe': 'Stripe price',
  'admin.plans.col_status': 'Status',
  'admin.plans.edit': 'Edit',
  'admin.plans.edit_title': 'Edit plan {name}',
  'admin.plans.description': 'Description',
  'admin.plans.price_cents': 'Price (cents/month)',
  'admin.plans.currency': 'Currency',
  'admin.plans.stripe_price_id': 'Stripe price ID',
  'admin.plans.unlimited': '∞',
  'admin.plans.all_languages': 'All',
  'admin.plans.free': 'Free',
  'admin.plans.no_stripe': 'not set',
  'admin.plans.active': 'Active',
  'admin.plans.inactive': 'Inactive',
  'admin.plans.saving': 'Saving…',
  'admin.plans.save': 'Save',
  'admin.plan.current': 'Current plan',
  'admin.plan.per_month': 'mo',
  'admin.plan.kitchen': 'Kitchen staff',
  'admin.plan.waiters': 'Waiters',
  'admin.plan.translations': 'Translations',
  'admin.plan.upgrade_sb': 'Get Small Business',
  'admin.plan.upgrade_pro': 'Get Pro',
  'admin.plan.manage': 'Manage subscription',
  'admin.plan.checkout_err': 'Could not start checkout.',
  'admin.plan.portal_err': 'Could not open the subscription portal.',
  'admin.import.outcome_title': 'Menu being built',
  'admin.import.outcome_hint': 'As we talk, the pages we read show up here.',
  'admin.import.outcome_hint_live':
    'This can take from tens of seconds to a few minutes — the AI is working; this screen updates by itself.',
  'admin.import.phase_reading': 'Reading {n} page(s) in parallel with AI…',
  'admin.import.phase_analyzing': 'AI assembling the menu and checking compatibility…',
  'admin.import.phase_reviewing': 'Applying your adjustments with AI…',
  'admin.import.page_chip': 'p. {n}',
  'admin.import.page_pending': 'Queued / being read',
  'admin.import.page_ocr_done': 'Read',
  'admin.import.page_error': 'Failed',
  'admin.import.tab_draft': 'Draft',
  'admin.import.tab_review': 'Detailed review',
  'admin.import.flags_title': '{n} item(s) need attention',
  'admin.import.flags_hint':
    'Each decision is applied to the draft instantly and the warning goes away.',
  'admin.import.flag_keep_extra': 'Keep "{extra}"',
  'admin.import.flag_remove_extra': 'Remove "{extra}"',
  'admin.import.flag_confirm_item': 'Looks right',
  'admin.import.flag_fix_item': 'Needs a fix',
  'admin.import.flags_apply_all': 'Apply suggestions',
  'admin.import.flags_keep_as_printed': 'Keep as printed',
  // #403 (final draft-warning panel spec): both options are acceptances —
  // document = keep the printed text; swap arrows = switch to the AI
  // suggestion. flag_confirm_item/flag_fix_item/flags_apply_all are OLD keys
  // already seeded (bank ⊆ source guard) — just unused here.
  'admin.import.flags_keep_all_printed': 'Keep all as printed',
  'admin.import.flags_switch_all': 'Switch all to suggestion',
  'admin.import.flag_switch': 'Switch to suggestion',
  'admin.import.flag_keep_printed': 'Keep as printed',
  'admin.import.flag_resolved_toast': '{n} warning(s) resolved.',
  'admin.import.link_title': 'Digital menu by link',
  'admin.import.link_button': 'Import link',
  'admin.import.link_hint':
    'Paste the online menu address (site, page or published PDF). Apps that only open on the phone do not serve the menu over the link — use photos.',
  'admin.import.step_ocr': 'Read the pages',
  'admin.import.step_agent': 'Understand the menu',
  'admin.import.step_semantic': 'Validate the reading',
  'admin.import.step_done': 'Draft ready',
  'admin.import.fix_progress': '{done} of {total} adjustments applied',
  'admin.import.kick': 'Force resume',
  'admin.import.kick_done': 'Resumed — the process continues in the background.',
  'admin.import.kick_stale_hint': 'No progress for {s}s — force resume.',
  'admin.import.awaiting_title': 'Waiting for your answer',
  'admin.import.awaiting_photo_fallback':
    "Page {n} wasn't read properly. Send a new photo of it or continue without it.",
  'admin.import.awaiting_input_fallback':
    'The assistant needs an answer from you to continue. Reply in the chat or continue without answering.',
  'admin.import.awaiting_send_photo': 'Send a new photo',
  'admin.import.awaiting_skip': 'Continue without this page',
  'admin.import.awaiting_continue': 'Continue without answering',
  'admin.import.awaiting_cancel': 'Cancel import',
  'admin.import.awaiting_hint': 'The process is paused waiting for you — it is not stuck.',
  // #491 (PR-2): «Continue where you left off» section — resumable imports on
  // import phase 1 (UX copy; {date}/{pages} interpolated in the component
  // inside <bdi> for RTL scripts). resume_dishes stays seeded for the
  // follow-up («Pratos lidos»), unused by the component in v1.
  'admin.import.resume_title': 'Pick up where you left off',
  'admin.import.resume_cta': 'Continue import',
  'admin.import.resume_cta_aria': 'Continue import from {date}',
  'admin.import.resume_meta': '{source} · Pages: {pages}',
  'admin.import.resume_dishes': 'Dishes read: {n}',
  'admin.import.resume_source_photos': 'Photos or PDF',
  'admin.import.resume_source_link': 'Link',
  'admin.import.resume_status_ready': 'Ready to review',
  'admin.import.resume_status_working': 'In progress',
  'admin.import.start_new': 'Or start a new import',
  'admin.import.start_new_hint':
    "A new import reads the Menu again and doesn't keep the corrections you already made.",
  // #299 crit. 9: rótulo próprio distingue o idioma do Menu do do restaurante
  'admin.import.menu_locale_label': 'Menu language',
  'admin.import.restaurant_locale_label': 'Restaurant language',
  // #299 crit. 7 (DECISÃO B, textos UX 6392564531): aviso bloqueante com as
  // duas saídas — (a) confirmar a moeda do restaurante; (b) fale com a equipe
  'admin.import.currency_mismatch':
    "The prices in this Menu were read in {detected}, but the restaurant uses {restaurant}. Pratzu doesn't convert prices. If the prices really are in {restaurant}, use the button below. If they are in {detected}, contact the Pratzu team to change the restaurant's currency before importing.",
  'admin.import.currency_mismatch_deduced':
    "The Menu is in {menu} and doesn't show the currency; Pratzu deduced {detected}, but the restaurant uses {restaurant}. Pratzu doesn't convert prices. If the prices really are in {restaurant}, use the button below. If they are in {detected}, contact the Pratzu team to change the restaurant's currency before importing.",
  'admin.import.currency_confirm_restaurant': 'Prices are in {currency}',
  'admin.import.currency_restaurant_missing':
    "The restaurant doesn't have a country and currency yet. Contact the Pratzu team to set them before importing this Menu.",
  'admin.import.currency_confirm_to_import': 'Resolve the currency to import',
  'admin.import.currency_confirmed_line': 'Currency confirmed: {currency} ✓',
  'admin.import.currency_line':
    'Menu currency as read: {detected} · Restaurant currency: {restaurant}',
  'admin.import.currency_line_deduced':
    'The Menu shows no currency; the restaurant uses {restaurant}.',
  // (UX) 422 de moeda/idioma mostrados na língua do painel
  'admin.import.error_currency_not_restaurant':
    'Only the restaurant currency ({restaurant}) can be confirmed.',
  'admin.import.error_currency_invalid': 'Unsupported currency.',
  'admin.import.error_locale_confirmation': 'Confirm the Menu language before importing.',
  'admin.import.locale_translations_note':
    'Dishes come in {menu}; translations into {restaurant} come later.',
  'admin.import.service_per_person': 'person',
  'admin.import.service_per_bill': 'bill',
  'admin.import.awaiting_photo_page': 'Photo of page',
  'admin.import.photo_request_failed':
    'I couldn\'t read page {n} (the reading failed). Send a new photo of it with the 📷 button, or tap "{skip}".',
  'admin.import.photo_request_poor':
    'Page {n} was hard to read. Send a new photo of it with the 📷 button, or tap "{skip}".',
  'admin.import.needs_attention': 'Needs attention',
  'admin.import.chat_done_hint': 'Chat finished — adjust in the review tab.',
  'admin.platform.users_load_err': 'Failed to load users',

  // #336 — página "Traduções" do restaurante (fora do Branding)
  'admin.translations.title': 'Translations',
  'admin.translations.subtitle': 'Menu languages and automatic AI translations',
  'admin.translations.coverage_title': 'Dictionary coverage by language',
  'admin.translations.coverage_desc':
    'Interface texts (panel, menus and login screen) translated in each catalog language — source: Portuguese.',
  'admin.translations.coverage_complete': 'Complete',
  'admin.translations.coverage_missing': '{count} missing',
  'admin.translations.base_title': 'Base translations (interface)',
  'admin.translations.base_desc':
    'Interface texts shared by all menus (buttons, cart, checkout…). Source: ui_texts table — {count} language base(s) in the catalog.',
  'admin.translations.base_refresh': 'Start/Update base translations',
  'admin.translations.base_force': 'Force re-translation (redoes AI drafts)',
  'admin.translations.base_hint':
    '"Start/Update" fills in the catalog languages that do not have a dictionary yet (1 AI request per language). "Force" discards AI drafts first and re-translates — manually reviewed texts are never changed.',
  'admin.translations.per_tenant_title': 'Translations per restaurant',
  'admin.translations.per_tenant_desc':
    'Re-translates the full menu (products, categories, ingredients, variations, extras and description) of the chosen restaurant, starting from its primary language.',
  'admin.translations.empty_title': 'No restaurants',
  'admin.translations.empty_hint': 'Create a restaurant to manage translations.',
  'admin.translations.primary_locale': 'primary language {locale}',
  'admin.translations.update_button': 'Update translations',
  'admin.translations.runs_title': 'Recent runs',
  'admin.translations.runs_desc':
    'Base = interface dictionary (platform) · Menu = the content of one restaurant. Click Logs to see the events of each run.',
  'admin.translations.base_started_toast':
    'Base translations started — {count} language(s): {bases}',
  'admin.translations.tenant_started_toast':
    '{name}: re-translation started — {count} AI draft(s) regenerated',
  'admin.translations.tenant_fallback': 'restaurant',

  'admin.tenant.new': 'New restaurant',
  'admin.tenant.create': 'Create restaurant',
  'admin.tenant.creating': 'Creating…',
  'admin.tenant.created_ok': 'Restaurant created successfully',
  'admin.tenant.created_err': 'Could not create the restaurant. Try again.',
  'admin.tenant.name': 'Restaurant name',
  'admin.tenant.name_ph': 'e.g. Pizza Roma',
  'admin.tenant.slug': 'Menu address',
  'admin.tenant.slug_ph': 'e.g. pizza-roma',
  'admin.tenant.slug_hint': 'Lowercase letters, numbers and hyphens (3 to 40).',
  'admin.tenant.slug_preview': 'pratzu.com/r/{slug}',
  'admin.tenant.slug_err_reserved': 'This address is reserved.',
  'admin.tenant.slug_err_format':
    'Use 3 to 40 lowercase letters, numbers or hyphens, with no hyphen at the start or end.',
  'admin.tenant.slug_err_too_long': 'Use at most 40 characters.',
  'admin.tenant.slug_err_taken': 'This address is already in use.',
  'admin.tenant.slug_use_suggestion': 'Use {suggestion}',
  'admin.tenant.country': 'Country',
  'admin.tenant.locale': 'Language',
  'admin.tenant.owner_email': 'Owner e-mail (optional)',
  'admin.tenant.owner_email_ph': 'owner@restaurant.com',
  'admin.tenant.owner_email_hint': 'This person gets owner access.',
  'admin.tenant.name_required': 'Restaurant name is required.',
  'admin.tenant.owner_email_invalid': 'Enter a valid e-mail address.',

  // ── concierge ideas (platform page) ──
  'admin.ideas.nav': 'Concierge ideas',
  'admin.ideas.title': 'Concierge ideas',
  'admin.ideas.desc': 'Conversations from the PRATZU page chat — what people want to make easier.',
  'admin.ideas.empty_title': 'No conversations yet',
  'admin.ideas.empty_hint':
    'Chat conversations from the institutional page appear here with summary and tags.',
  'admin.ideas.no_summary': 'No summary (unfinished conversation)',
  'admin.ideas.messages': 'Messages',
  'admin.ideas.show': 'View conversation',
  'admin.ideas.hide': 'Close',

  // ── contact messages (platform page) ──
  'admin.contact.nav': 'Contact messages',
  'admin.countries.nav': 'Countries',
  'admin.countries.title': 'Countries',
  'admin.countries.desc':
    'Multi-language menu sign-up and full version (orders, POS and till) per country.',
  'admin.countries.summary':
    'Full version approved in: {homologated}. Sign-up closed in: {closed}.',
  'admin.countries.search': 'Search country (name or code)',
  'admin.countries.only_exceptions': 'Only approved or closed countries',
  'admin.countries.country': 'Country',
  'admin.countries.signup': 'Sign-up open',
  'admin.countries.homologated': 'Full version',
  'admin.countries.save_error': 'Could not save the country.',
  'admin.billing_cfg.title': 'Trial and billing',
  'admin.billing_cfg.desc':
    'Free trial length (no card), grace period for late payments, and trial rules.',
  'admin.billing_cfg.trial_days': 'Trial days',
  'admin.billing_cfg.past_due_grace_days': 'Grace days (late payment)',
  'admin.billing_cfg.trial_once_per_tenant': 'One trial per restaurant',
  'admin.billing_cfg.trial_once_per_owner_email': 'One trial per owner email',
  'admin.billing_cfg.existing_tenants_new_trial': 'Existing restaurants get a new free trial',
  'admin.billing_cfg.save': 'Save',
  'admin.billing_cfg.saved': 'Settings saved.',
  'admin.billing_cfg.save_error': 'Could not save the settings.',
  'admin.leads.nav': 'Leads',
  'admin.leads.title': 'Leads',
  'admin.leads.desc':
    'Requests for more information about Small Business and Pro, and “notify me” requests for countries not open yet.',
  'admin.leads.filter_status': 'Status',
  'admin.leads.filter_plan': 'Plan',
  'admin.leads.filter_country': 'Country',
  'admin.leads.all': 'All',
  'admin.leads.status_new': 'New',
  'admin.leads.status_contacted': 'Contacted',
  'admin.leads.status_closed': 'Closed',
  'admin.leads.status_spam': 'Spam',
  'admin.leads.plan_small_business': 'Small Business',
  'admin.leads.plan_pro': 'Pro',
  'admin.leads.plan_notify': 'Notify me',
  'admin.leads.detected': 'detected',
  'admin.leads.not_notified': 'sales email not sent',
  'admin.leads.empty_title': 'No leads',
  'admin.leads.empty_hint': 'Requests from the landing page and the console show up here.',
  'admin.leads.save_error': 'Could not update the lead.',
  'admin.plan.activate_bundle': 'Subscribe to the multi-language menu',
  'admin.plan.info_sb': 'More information: Small Business',
  'admin.plan.info_pro': 'More information: Pro',
  'admin.plan.status_trial': 'Free trial until {date}: your public menu is online.',
  'admin.plan.status_grace': 'Payment overdue: your public menu stays online until {date}.',
  'admin.plan.status_inactive':
    'Public menu paused: only you can see the preview. Subscribe for {price}/month to put it back online.',
  'admin.plan.status_inactive_noprice':
    'Public menu paused: only you can see the preview. Subscribe to the multi-language menu to put it back online.',
  'admin.lead_modal.title': 'More information about {plan}',
  'admin.lead_modal.plan_sb': 'Small Business',
  'admin.lead_modal.plan_pro': 'Pro',
  'admin.lead_modal.intro':
    'Pricing depends on your country’s tax setup. Leave your name and email and the PRATZU sales team will get back to you.',
  'admin.lead_modal.name': 'Your name',
  'admin.lead_modal.email': 'Email',
  'admin.lead_modal.country': 'Country',
  'admin.lead_modal.consent': 'I agree to be contacted by PRATZU about this request.',
  'admin.lead_modal.consent_note':
    'We keep your name, email, country, language and the date of this consent only so our sales team can reply to this request. To correct or delete your data, write to sales@pratzu.com.',
  'admin.lead_modal.send': 'Send request',
  'admin.lead_modal.close': 'Close',
  'admin.lead_modal.sent': 'Thanks! The PRATZU sales team will reply by email soon.',
  'admin.lead_modal.unavailable': 'Contact requests open soon.',
  'admin.lead_modal.turnstile_wait': 'One moment: we’re verifying your request.',
  'admin.lead_modal.turnstile_error': 'We couldn’t verify the request. Please try again.',
  'admin.lead_modal.err_required': 'This field is required.',
  'admin.lead_modal.err_email': 'Enter a valid email address.',
  'admin.lead_modal.err_invalid': 'Please check this field.',
  'admin.lead_modal.err_consent': 'Please accept to continue.',
  'admin.lead_modal.err_validation': 'Please check the highlighted fields.',
  'admin.lead_modal.err_rate': 'Too many attempts. Please try again in a few minutes.',
  'admin.lead_modal.err_generic': 'Could not send. Please try again.',
  'admin.contact.title': 'Contact messages',
  'admin.contact.desc':
    'PRATZU page form — questions and requests that arrive without going through email.',
  'admin.contact.empty_title': 'No messages yet',
  'admin.contact.empty_hint': 'Messages sent through the site form appear here.',
  'admin.contact.new': 'New',
  'admin.contact.handled': 'Handled',
  'admin.contact.mark_handled': 'Mark as handled',
  'admin.contact.reopen': 'Reopen',
  'admin.contact.toggle_error': 'Could not update the message.',

  // ── menu management (Menu.tsx) ──
  'admin.channel.dine_in': '🍽 Table',
  'admin.menu.title': 'Menu',
  'admin.menu.subtitle': 'Manage categories and products',
  'admin.menu.add_category': '+ Category',
  'admin.menu.add_product': '+ Product',
  'admin.menu.all': 'All',
  'admin.menu.empty_title': 'No products yet',
  'admin.menu.empty_desc': 'Create the first product for this menu',
  'admin.menu.create_category_first': '+ Category first',
  'admin.menu.available': 'Available',
  'admin.menu.unavailable': 'Unavailable',
  'admin.menu.live': 'Live',
  'admin.menu.paused': 'Paused',
  'admin.menu.no_price': 'No price',
  // #489 M1 (item 3): aviso ao LIGAR o «No ar» com prato a 0 (ou variante
  // disponível a 0) — textos propostos pelo UX, a fechar com o PO no PR
  'admin.menu.zero_price_title': 'This dish has no price',
  'admin.menu.zero_price_body':
    "Once live, customers can order this dish for {price}. Fix the price or confirm it's free.",
  'admin.menu.zero_price_option_body':
    "Once live, customers can choose {name} for {price}. Fix the price or confirm it's free.",
  'admin.menu.zero_price_fix': 'Fix price',
  'admin.menu.zero_price_go_live': 'Make live at {price}',
  'admin.menu.new_category': 'New category',
  'admin.menu.edit_category': 'Edit category',
  'admin.menu.category_updated': 'Category updated!',
  'admin.menu.category_created': 'Category created!',
  'admin.menu.category_deactivated': 'Category deactivated',
  'admin.menu.deactivate': 'Deactivate',
  'admin.menu.name': 'Name',
  'admin.menu.sort_order': 'Display order',
  'admin.menu.sort_hint': 'Lower number shows first',
  'admin.menu.new_product': 'New product',
  'admin.menu.edit_product': 'Edit product',
  'admin.menu.create_product': 'Create product',
  // legacy (#403): the editor now uses the single text_language indicator —
  // key KEPT in the source because it is already seeded in the DB (bank ⊆ source guard)
  'admin.menu.name_locale': 'Name ({locale})',
  // #403: single language indicator for the Name/Description pair in the editor
  'admin.menu.text_language': 'Text language: {locale}',
  // #387: product with no line in the restaurant's primary language yet
  'admin.menu.no_source_translation':
    "This product doesn't have a name in {locale} yet. Write the name and description in the restaurant's main language.",
  'admin.menu.price': 'Price',
  'admin.menu.price_hint': 'In {currency}',
  // #479: dica do campo derivado (com variantes) — texto validado pelo UX
  'admin.menu.price_from_variants_hint':
    'Set by the variants: the lowest price among the available ones. To change it, edit the variant prices.',
  'admin.menu.variant_label': 'Variants label',
  'admin.menu.variant_label_ph': 'e.g. Size, Portion, Weight',
  'admin.menu.variant_label_hint': 'Leave empty if the product has no variants',
  'admin.menu.description': 'Description',
  'admin.menu.desc_ph': 'Ingredients and dish description...',
  'admin.menu.category': 'Category',
  'admin.menu.tax_category': 'Tax category',
  'admin.menu.tax_food': 'Food (food)',
  'admin.menu.tax_beverage': 'Beverage (beverage)',
  'admin.menu.tax_tobacco': 'Tobacco (tobacco)',
  'admin.menu.tax_other': 'Other',
  'admin.menu.available_in': 'Available in',
  'admin.menu.takeaway': '🛍 Take-away',
  'admin.menu.delivery': '🚚 Delivery',
  'admin.menu.product_created': 'Product created! You can now add ingredients and extras.',
  'admin.menu.product_updated': 'Product updated!',
  'admin.menu.create_product_first': 'Create the product first to add ingredients and extras.',
  'admin.menu.variants': 'Variants',
  'admin.menu.no_variants': 'No variants registered',
  'admin.menu.variant_ph': 'e.g. Small',
  'admin.menu.ingredients': 'Ingredients',
  'admin.menu.no_ingredients': 'No ingredients registered',
  'admin.menu.allergens': 'Allergens',
  'admin.menu.allergens_hint':
    'The 14 EU allergens (Reg. 1169/2011). The AI suggests them; only the list you confirm counts as the dish’s information.',
  'admin.menu.allergens_unconfirmed': 'Not confirmed',
  'admin.menu.allergens_confirmed': 'Confirmed',
  'admin.menu.allergens_from_menu': 'Read from the menu',
  'admin.menu.allergens_from_ai': 'Suggested by AI',
  'admin.menu.allergens_confirm': 'Confirm allergens',
  'admin.menu.allergens_none': 'No allergens',
  'admin.menu.allergens_saved': 'Allergens confirmed',
  'admin.menu.allergens_pending': 'Allergens to confirm',
  'admin.menu.allergens_unreviewed': 'To review',
  'admin.menu.allergens_unreviewed_hint':
    'No allergens suggested. Review and confirm — even if the dish has none.',
  'admin.menu.allergens_reopen_note': 'Changing the ingredients reopens the allergen confirmation.',
  'admin.menu.ingredient_suggestions': 'Likely ingredients',
  'admin.menu.ingredient_suggestions_hint':
    'Suggested by AI. They are only added to the dish once accepted.',
  'admin.menu.ingredient_accept': 'Accept',
  'admin.menu.ingredient_dismiss': 'Dismiss',
  'admin.menu.ingredients_pending': 'Ingredients to confirm',
  // ── dish attributes (#214/#216: diets, spice, tags) ──
  'admin.menu.attributes': 'Attributes',
  'admin.menu.attributes_hint':
    'Diets, spice level and flavor tags. AI suggestions come pre-checked — only "Confirm attributes" publishes them.',
  'admin.menu.attributes_pending': 'Attributes to confirm',
  'admin.menu.attributes_confirm': 'Confirm attributes',
  'admin.menu.attributes_saved': 'Attributes confirmed.',
  'admin.menu.attributes_diet': 'Diets',
  'admin.menu.attributes_spice': 'Spice level',
  'admin.menu.attributes_flavor': 'Flavor tags',
  'admin.menu.attributes_flavor_ph': 'e.g. creamy',
  'admin.menu.attributes_kids': "Kids' dish",
  'admin.menu.attributes_signature': 'Signature dish',
  'admin.menu.attributes_suggest': 'Suggest with AI',
  'admin.menu.attributes_suggesting': 'Suggesting…',
  'admin.menu.attributes_ai_hint': 'AI suggestion — review and confirm before publishing.',
  'admin.menu.attributes_state_confirmed': 'Attributes: confirmed',
  'admin.menu.attributes_state_pending': 'Attributes: to confirm',
  'admin.menu.attributes_state_none': 'Attributes: to review',
  'admin.menu.spice_0': 'No heat',
  'admin.menu.spice_1': '🌶 mild',
  'admin.menu.spice_2': '🌶🌶 medium',
  'admin.menu.spice_3': '🌶🌶🌶 hot',
  // ── "Review attributes" screen (#216) ──
  'admin.nav.review_attributes': 'Review attributes',
  'admin.attributes_review.title': 'Review attributes',
  'admin.attributes_review.subtitle':
    'Allergens, diets, spice and tags in one flow — 5 s per dish.',
  'admin.attributes_review.progress': '{done} of {total} reviewed',
  'admin.attributes_review.empty_title': 'All reviewed!',
  'admin.attributes_review.empty_desc': 'No dishes with pending suggestions.',
  'admin.attributes_review.open_menu': 'Go to menu',
  'admin.attributes_review.skip': 'Skip this one',
  'admin.menu.review_attributes_link': 'Review attributes ({n})',
  // #209/#216 (0106): prato do dia — mesmos textos das seeds pt/en do banco
  'admin.menu.special': 'Dish of the day',
  'admin.menu.special_hint':
    "Today's highlight on the menu — no special price (a different price is a promotion). Max 2 per day.",
  'admin.menu.special_today': 'Today only',
  'admin.menu.special_weekly': 'Weekly on days',
  'admin.menu.special_range': 'From a date to a date',
  'admin.menu.special_active': 'Is dish of the day',
  'admin.menu.special_saved': 'Dish of the day saved.',
  'admin.menu.special_limit':
    'There are already 2 dishes of the day in this window — deactivate one before highlighting another.',
  'admin.menu.special_shortcut': 'Mark as dish of the day (today only)',
  'admin.import.review_allergens_open': 'Review allergens',
  'admin.import.review_allergens_undo': 'Undo confirmation',
  'admin.import.review_confirm_note':
    'Suggested allergens and ingredients are saved as “to confirm”. Confirm them here or later in the dish editor.',
  'admin.menu.removable': 'Removable',
  'admin.menu.fixed': 'Fixed',
  'admin.menu.toggle_removable_aria': 'Toggle removable: {name}',
  'admin.menu.ingredient_ph': 'Ingredient name',
  'admin.menu.extras': 'Paid extras',
  'admin.menu.no_extras': 'No extras registered',
  'admin.menu.extra_ph': 'e.g. Extra cheese',
  'admin.menu.images': 'Images',
  'admin.menu.no_images': 'No images registered',
  'admin.menu.uploading': 'Uploading...',
  'admin.menu.add_image': 'Add image',
  'admin.menu.primary_image': 'Primary image',
  'admin.menu.remove_image': 'Remove image',

  // ── product promotion (#205, spec §8.1) ──
  'admin.promo.section': 'Promotion',
  'admin.promo.none': 'No promotion',
  'admin.promo.type': 'Discount type',
  'admin.promo.type_percent': 'Percentage (%)',
  'admin.promo.type_fixed': 'Final price',
  'admin.promo.value_percent': 'Discount (%)',
  'admin.promo.value_fixed': "Item's final price",
  'admin.promo.variant': 'Variant',
  'admin.promo.variant_required':
    'A final price on a product with variants requires choosing the variant.',
  'admin.promo.days': 'Days of the week',
  'admin.promo.start_time': 'Starts',
  'admin.promo.end_time': 'Ends',
  'admin.promo.ends_at': 'End date (optional)',
  'admin.promo.window_hint': 'Times in the restaurant’s timezone ({timezone}).',
  'admin.promo.label': 'Label (optional)',
  'admin.promo.preview_invalid': 'Adjust the discount to see the price preview.',
  'admin.promo.create': 'Create promotion',
  'admin.promo.created': 'Promotion created!',
  'admin.promo.updated': 'Promotion updated!',
  'admin.promo.turn_off': 'Turn off',
  'admin.promo.turn_on': 'Turn on',
  'admin.promo.state_percent': 'Promo −{value}%',
  'admin.promo.state_fixed': 'Promo {price}',
  'admin.promo.state_scheduled': 'Scheduled',
  'admin.promo.state_expired': 'Expired',
  'admin.promo.state_off': 'Off',
  'admin.promo.shortcut_title': 'Product promotion',

  // ── waiter order modal (OrderFormModal) ──
  'admin.orderForm.edit_title': 'Edit order {id}',
  'admin.orderForm.new_title': 'New order',
  'admin.orderForm.save_changes': 'Save changes',
  'admin.orderForm.submit': 'Send order',
  'admin.orderForm.table': 'Table',
  'admin.orderForm.select_table': 'Select a table…',
  'admin.orderForm.table_n': 'Table {n}',
  'admin.orderForm.need_item': 'Add at least one item',
  'admin.orderForm.need_table': 'Choose a table',
  'admin.orderForm.need_reference': 'Enter the reference (groups the table orders in the kitchen)',
  'admin.orderForm.updated': 'Order updated',
  'admin.orderForm.created': 'Order created',
  'admin.orderForm.save_error': 'Error saving order',
  'admin.orderForm.add_items': 'Add items',
  'admin.orderForm.search_ph': 'Search product…',
  'admin.orderForm.customizable': 'customizable',
  'admin.orderForm.no_products': 'No products found',
  'admin.orderForm.reference': 'Reference *',
  'admin.orderForm.reference_ph': 'e.g. customer name — groups orders in the kitchen',
  'admin.orderForm.notes': 'Notes',
  'admin.orderForm.optional': 'Optional',
  'admin.orderForm.edit_hint':
    'Customizations of existing items are preserved when editing. Orders can only be edited before preparation starts.',
  'admin.orderForm.variation': 'Variation',
  'admin.orderForm.remove_ingredients': 'Remove ingredients',
  'admin.orderForm.without': 'without',
  'admin.orderForm.tap_to_remove': 'Tap to remove from the item',
  'admin.orderForm.optionals': 'Optional items',
  'admin.orderForm.qty': 'Qty',
  'admin.orderForm.choose_variation': 'Choose a variation',
  'admin.orderForm.add_price': 'Add — {price}',
}
